import { getCountryRules } from "../country/registry";
import { TaxBracket } from "../country/types";
import { calculateCanadaHomeRebates } from "./canadaHomeRebate";

export interface TaxCalculationResult {
  grossIncome: number;
  standardDeduction: number;
  taxableIncome: number;
  incomeTax: number;
  socialSecurity: number;
  additionalTaxes: Record<string, number>;
  totalTax: number;
  takeHomePay: number;
  effectiveTaxRate: number;
  marginalTaxRate: number;
  breakdown: Array<{ label: string; amount: number }>;
}

/**
 * Calculates progressive tax based on brackets (internal helper)
 */
function calculateBracketsTax(taxableIncome: number, brackets: TaxBracket[]): { tax: number; marginalRate: number } {
  let totalTax = 0;
  let marginalRate = 0;

  for (let i = 0; i < brackets.length; i++) {
    const bracket = brackets[i];
    const nextBracket = brackets[i + 1];
    
    const min = bracket.min;
    const max = bracket.max ?? Infinity;
    
    if (taxableIncome > min) {
      const taxableInThisBracket = Math.min(taxableIncome - min, max - min);
      totalTax += taxableInThisBracket * bracket.rate;
      marginalRate = bracket.rate;
    } else {
      break;
    }
  }

  return { tax: totalTax, marginalRate };
}

/**
 * Main Income Tax Calculator Engine
 */
// Helper functions for country-specific tax calculations

function calculateUSTax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>,
  options: { filingStatus?: string; stateTaxRate?: number }
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  const statusId = options.filingStatus || "single";
  const statusConfig = rules.tax.incomeTax.filingStatuses?.find((s) => s.id === statusId) 
    || rules.tax.incomeTax.filingStatuses?.[0];

  if (!statusConfig) {
    throw new Error(`Filing status configuration not found for: ${statusId}`);
  }

  const standardDeduction = statusConfig.standardDeduction;
  const taxableIncome = Math.max(0, grossIncome - standardDeduction);

  const taxCalc = calculateBracketsTax(taxableIncome, statusConfig.brackets);
  const incomeTax = taxCalc.tax;
  const marginalTaxRate = taxCalc.marginalRate;

  // FICA (Social Security + Medicare)
  const ssRate = rules.tax.socialSecurity?.employeeRate;
  const ssCap = rules.tax.socialSecurity?.cap;
  if (ssRate === undefined || ssCap === undefined) {
    throw new Error("US Social Security rate or cap is not configured in country rules");
  }
  const ssTax = ssCap === null ? (grossIncome * ssRate) : Math.min(grossIncome, ssCap) * ssRate;
  
  const medRate = rules.tax.countrySpecific?.medicareRate;
  const additionalMedRate = rules.tax.countrySpecific?.additionalMedicareRate;
  const additionalMedThreshold = statusId === "married_jointly" 
    ? rules.tax.countrySpecific?.additionalMedicareThresholdMarried 
    : rules.tax.countrySpecific?.additionalMedicareThresholdSingle;
  
  if (medRate === undefined || additionalMedRate === undefined || additionalMedThreshold === undefined) {
    throw new Error("US Medicare rates or thresholds are not configured in country rules");
  }
  
  const basicMedicare = grossIncome * medRate;
  const additionalMedicare = Math.max(0, grossIncome - additionalMedThreshold) * additionalMedRate;
  const socialSecurity = ssTax + basicMedicare + additionalMedicare;

  const additionalTaxes: Record<string, number> = {};
  const breakdown: Array<{ label: string; amount: number }> = [];
  
  // Optional State Tax
  if (options.stateTaxRate && options.stateTaxRate > 0) {
    const stateTax = taxableIncome * (options.stateTaxRate / 100);
    additionalTaxes["State Income Tax"] = stateTax;
  }

  breakdown.push({ label: "Federal Income Tax", amount: incomeTax });
  breakdown.push({ label: "FICA Social Security", amount: ssTax });
  breakdown.push({ label: "FICA Medicare", amount: basicMedicare + additionalMedicare });
  if (additionalTaxes["State Income Tax"]) {
    breakdown.push({ label: "State Income Tax", amount: additionalTaxes["State Income Tax"] });
  }

  return { incomeTax, socialSecurity, additionalTaxes, breakdown, taxableIncome, standardDeduction, marginalTaxRate };
}

function calculateUKTax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  const defaultAllowance = rules.tax.incomeTax.personalAllowance;
  const taperThreshold = rules.tax.countrySpecific?.allowanceTaperThreshold;
  const niThreshold = rules.tax.countrySpecific?.niLowerThreshold;
  const niUpperLimit = rules.tax.socialSecurity?.cap;
  const basicNiRate = rules.tax.socialSecurity?.employeeRate;
  const upperNiRate = rules.tax.countrySpecific?.niUpperRate;

  if (
    defaultAllowance === undefined ||
    taperThreshold === undefined ||
    niThreshold === undefined ||
    niUpperLimit === undefined ||
    basicNiRate === undefined ||
    upperNiRate === undefined
  ) {
    throw new Error("UK tax or National Insurance parameters are not configured in country rules");
  }

  let personalAllowance = defaultAllowance;
  if (grossIncome > taperThreshold) {
    personalAllowance = Math.max(0, defaultAllowance - Math.floor((grossIncome - taperThreshold) / 2));
  }

  const taxableIncome = Math.max(0, grossIncome - personalAllowance);
  const incomeBrackets = rules.tax.incomeTax.brackets || [];
  const higherRateThreshold = niUpperLimit ?? 50270;
  const additionalRateThreshold = incomeBrackets.find((bracket) => bracket.max === null)?.min ?? 125140;
  const basicRate = incomeBrackets.find((bracket) => bracket.rate === 0.2)?.rate ?? 0.2;
  const higherRate = incomeBrackets.find((bracket) => bracket.rate === 0.4)?.rate ?? 0.4;
  const additionalRate = incomeBrackets.find((bracket) => bracket.rate === 0.45)?.rate ?? 0.45;
  const basicBand = Math.max(0, higherRateThreshold - defaultAllowance);
  const additionalBandStart = Math.max(basicBand, additionalRateThreshold - personalAllowance);
  const taxCalc = calculateBracketsTax(taxableIncome, [
    { min: 0, max: basicBand, rate: basicRate, label: "Basic Rate" },
    { min: basicBand, max: additionalBandStart, rate: higherRate, label: "Higher Rate" },
    { min: additionalBandStart, max: null, rate: additionalRate, label: "Additional Rate" },
  ]);

  let niTax = 0;
  if (grossIncome > niThreshold) {
    const basicLimit = niUpperLimit === null ? grossIncome : niUpperLimit;
    const basicNi = Math.max(0, Math.min(grossIncome, basicLimit) - niThreshold) * basicNiRate;
    const upperNi = niUpperLimit === null ? 0 : Math.max(0, grossIncome - niUpperLimit) * upperNiRate;
    niTax = basicNi + upperNi;
  }

  const breakdown: Array<{ label: string; amount: number }> = [
    { label: "Income Tax", amount: taxCalc.tax },
    { label: "National Insurance", amount: niTax },
  ];

  return {
    incomeTax: taxCalc.tax,
    socialSecurity: niTax,
    additionalTaxes: {},
    breakdown,
    taxableIncome,
    standardDeduction: personalAllowance,
    marginalTaxRate: taxCalc.marginalRate,
  };
}

function calculateAUTax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>,
  options: { calculateHecs?: boolean }
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  const taxableIncome = grossIncome;
  const taxCalc = calculateBracketsTax(taxableIncome, rules.tax.incomeTax.brackets || []);
  const incomeTax = taxCalc.tax;
  const marginalTaxRate = taxCalc.marginalRate;

  const medicareLevyRate = rules.tax.countrySpecific?.medicareLevy ?? rules.tax.socialSecurity?.employeeRate;
  if (medicareLevyRate === undefined) {
    throw new Error("Australia Medicare Levy rate is not configured in country rules");
  }

  const medicareLevy = grossIncome * medicareLevyRate;
  const additionalTaxes: Record<string, number> = { "Medicare Levy": medicareLevy };
  const breakdown: Array<{ label: string; amount: number }> = [
    { label: "Income Tax", amount: incomeTax },
    { label: "Medicare Levy", amount: medicareLevy },
  ];

  if (options.calculateHecs) {
    const hecsBrackets = rules.tax.countrySpecific?.hecsHelpBrackets;
    if (!hecsBrackets) {
      throw new Error("Australia HELP repayment brackets are not configured in country rules");
    }

    let hecsRepayment = 0;
    if (rules.tax.countrySpecific?.hecsHelpRepaymentMode === "marginal") {
      for (const bracket of hecsBrackets) {
        const min = bracket.min;
        const max = bracket.max ?? Infinity;
        if (grossIncome > min) {
          hecsRepayment += Math.min(grossIncome - min, max - min) * bracket.rate;
        }
      }
    } else {
      const activeBracket = [...hecsBrackets]
        .reverse()
        .find((bracket: any) => grossIncome >= bracket.min && (bracket.max === null || grossIncome <= bracket.max));
      hecsRepayment = activeBracket ? grossIncome * activeBracket.rate : 0;
    }
    additionalTaxes["HELP Repayment"] = hecsRepayment;
    breakdown.push({ label: "HELP Repayment", amount: hecsRepayment });
  }

  return { incomeTax, socialSecurity: 0, additionalTaxes, breakdown, taxableIncome, standardDeduction: 0, marginalTaxRate };
}

function calculateINTax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>,
  options: { taxRegime?: "old" | "new"; deductions80C?: number; additionalDeductions?: number }
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  const regime = options.taxRegime || "new";
  const regimeConfig = regime === "new" ? rules.tax.countrySpecific?.newRegime : rules.tax.countrySpecific?.oldRegime;

  if (!regimeConfig) {
    throw new Error(`India Tax Regime configuration not found for: ${regime}`);
  }

  const standardDeductionBase = regimeConfig.standardDeduction ?? 0;
  const deductions80C = regime === "old" ? Math.min(Math.max(0, options.deductions80C ?? 0), 150000) : 0;
  const additionalDeductions = regime === "old" ? Math.max(0, options.additionalDeductions ?? 0) : 0;
  const standardDeduction = standardDeductionBase + deductions80C + additionalDeductions;
  const taxableIncome = Math.max(0, grossIncome - standardDeduction);

  const taxCalc = calculateBracketsTax(taxableIncome, regimeConfig.brackets || []);
  let baseTax = taxCalc.tax;
  const marginalTaxRate = taxCalc.marginalRate;

  const rebateLimit = regimeConfig.rebateLimit || 0;
  const maxRebate = regimeConfig.maxRebate || 0;

  if (rebateLimit > 0 && maxRebate > 0) {
    if (taxableIncome <= rebateLimit) {
      baseTax = Math.max(0, baseTax - maxRebate);
    } else {
      const taxAtLimit = Math.max(0, calculateBracketsTax(rebateLimit, regimeConfig.brackets || []).tax - maxRebate);
      const excessIncome = taxableIncome - rebateLimit;
      const taxWithMarginalRelief = taxAtLimit + excessIncome;
      if (baseTax > taxWithMarginalRelief) {
        baseTax = taxWithMarginalRelief;
      }
    }
  }

  const cessRate = rules.tax.countrySpecific?.cessRate;
  if (cessRate === undefined) {
    throw new Error("India Health & Education Cess rate is not configured in country rules");
  }
  const cess = baseTax * cessRate;
  const incomeTax = baseTax + cess;

  const epfRate = rules.tax.socialSecurity?.employeeRate;
  const epfCap = rules.tax.socialSecurity?.cap;
  if (epfRate === undefined || epfCap === undefined) {
    throw new Error("India EPF rate or cap is not configured in country rules");
  }
  const epfTax = epfCap === null ? grossIncome * epfRate : Math.min(grossIncome / 12, epfCap) * epfRate * 12;
  const socialSecurity = epfTax;

  const breakdown: Array<{ label: string; amount: number }> = [
    { label: `Income Tax (${regime === "new" ? "New" : "Old"} Regime)`, amount: baseTax },
    { label: `Health & Education Cess (${cessRate * 100}%)`, amount: cess },
    { label: "EPF Contribution", amount: epfTax },
  ];

  if (regime === "old" && (deductions80C > 0 || additionalDeductions > 0)) {
    breakdown.unshift({ label: "Old Regime Deductions Applied", amount: -deductions80C - additionalDeductions });
  }

  return { incomeTax, socialSecurity, additionalTaxes: {}, breakdown, taxableIncome, standardDeduction, marginalTaxRate };
}

function calculateCATax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  const brackets = rules.tax.incomeTax.brackets || [];
  const taxCalc = calculateBracketsTax(grossIncome, brackets);
  const firstRate = brackets[0]?.rate ?? 0.14;
  const basicPersonalAmount = rules.tax.incomeTax.personalAllowance ?? 0;
  const incomeTax = Math.max(0, taxCalc.tax - basicPersonalAmount * firstRate);

  const cppRate = rules.tax.socialSecurity?.employeeRate;
  const cppMaxPensionable = rules.tax.socialSecurity?.cap;
  const cppBasicExemption = rules.tax.countrySpecific?.cppBasicExemption ?? 3500;
  const eiRate = rules.tax.countrySpecific?.eiRateEmployee;
  const eiCap = rules.tax.countrySpecific?.eiCap;

  if (cppRate === undefined || cppMaxPensionable === undefined || eiRate === undefined || eiCap === undefined) {
    throw new Error("Canada CPP or EI parameters are not configured in country rules");
  }

  const cppTax = cppMaxPensionable === null
    ? Math.max(0, grossIncome - cppBasicExemption) * cppRate
    : Math.max(0, Math.min(grossIncome, cppMaxPensionable) - cppBasicExemption) * cppRate;
  const eiTax = Math.min(grossIncome, eiCap) * eiRate;
  const socialSecurity = cppTax + eiTax;

  const breakdown: Array<{ label: string; amount: number }> = [
    { label: "Federal Income Tax after Basic Personal Amount", amount: incomeTax },
    { label: "Canada Pension Plan (CPP)", amount: cppTax },
    { label: "Employment Insurance (EI)", amount: eiTax },
  ];

  return {
    incomeTax,
    socialSecurity,
    additionalTaxes: {},
    breakdown,
    taxableIncome: grossIncome,
    standardDeduction: basicPersonalAmount,
    marginalTaxRate: taxCalc.marginalRate,
  };
}

function calculateDETax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>,
  options: { churchTax?: boolean }
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  const werbungskosten = rules.tax.countrySpecific?.werbungskosten;
  if (werbungskosten === undefined) {
    throw new Error("Germany Werbungskosten rate is not configured in country rules");
  }
  const taxableIncome = Math.max(0, grossIncome - werbungskosten);
  const taxCalc = calculateBracketsTax(taxableIncome, rules.tax.incomeTax.brackets || []);
  const incomeTax = taxCalc.tax;
  const marginalTaxRate = taxCalc.marginalRate;

  const additionalTaxes: Record<string, number> = {};
  const breakdown: Array<{ label: string; amount: number }> = [];

  // Solidaritätszuschlag
  let soli = 0;
  const soliThreshold = rules.tax.countrySpecific?.soliThresholdSingle;
  const soliRate = rules.tax.countrySpecific?.soliRate;
  if (soliThreshold === undefined || soliRate === undefined) {
    throw new Error("Germany Soli threshold or rate is not configured in country rules");
  }
  if (incomeTax > soliThreshold) {
    soli = incomeTax * soliRate;
    additionalTaxes["Solidaritätszuschlag"] = soli;
  }

  // Church Tax
  if (options.churchTax) {
    const churchTaxRate = rules.tax.countrySpecific?.churchTaxRate;
    if (churchTaxRate === undefined) {
      throw new Error("Germany Church Tax rate is not configured in country rules");
    }
    const church = incomeTax * churchTaxRate;
    additionalTaxes["Kirchensteuer"] = church;
  }

  // Social Security
  const ssRate = rules.tax.socialSecurity?.employeeRate;
  const ssCap = rules.tax.socialSecurity?.cap;
  if (ssRate === undefined || ssCap === undefined) {
    throw new Error("Germany social security rate or cap is not configured in country rules");
  }
  const ssTax = ssCap === null ? (grossIncome * ssRate) : Math.min(grossIncome, ssCap) * ssRate;
  const socialSecurity = ssTax;

  breakdown.push({ label: "Einkommensteuer (Income Tax)", amount: incomeTax });
  if (soli > 0) breakdown.push({ label: "Solidaritätszuschlag", amount: soli });
  if (additionalTaxes["Kirchensteuer"]) breakdown.push({ label: "Kirchensteuer", amount: additionalTaxes["Kirchensteuer"] });
  breakdown.push({ label: "Sozialabgaben (Social Security)", amount: ssTax });

  return { incomeTax, socialSecurity, additionalTaxes, breakdown, taxableIncome, standardDeduction: werbungskosten, marginalTaxRate };
}

function calculateNLTax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>,
  options: { thirtyPercentExpats?: boolean }
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  let adjustedGross = grossIncome;
  let standardDeduction = 0;
  
  if (options.thirtyPercentExpats) {
    adjustedGross = grossIncome * 0.70;
    standardDeduction = grossIncome * 0.30;
  }

  const taxableIncome = Math.max(0, adjustedGross);
  const taxCalc = calculateBracketsTax(taxableIncome, rules.tax.incomeTax.brackets || []);
  const baseTax = taxCalc.tax;
  const marginalTaxRate = taxCalc.marginalRate;

  // Tax Credits
  const genCreditMax = rules.tax.countrySpecific?.generalTaxCreditMax;
  const genCreditThreshold = rules.tax.countrySpecific?.generalTaxCreditThreshold;
  const genCreditFactor = rules.tax.countrySpecific?.generalTaxCreditFactor;

  const workCreditMax = rules.tax.countrySpecific?.workTaxCreditMax;
  const workCreditThreshold = rules.tax.countrySpecific?.workTaxCreditThreshold;
  const workCreditFactor = rules.tax.countrySpecific?.workTaxCreditFactor;

  if (
    genCreditMax === undefined || genCreditThreshold === undefined || genCreditFactor === undefined ||
    workCreditMax === undefined || workCreditThreshold === undefined || workCreditFactor === undefined
  ) {
    throw new Error("Netherlands tax credit limits are not configured in country rules");
  }

  let genCredit = 0;
  if (taxableIncome <= genCreditThreshold) {
    genCredit = genCreditMax;
  } else {
    genCredit = Math.max(0, genCreditMax - (taxableIncome - genCreditThreshold) * genCreditFactor);
  }

  let workCredit = 0;
  if (taxableIncome <= workCreditThreshold) {
    workCredit = taxableIncome === 0 ? 0 : Math.min(workCreditMax, workCreditMax * (taxableIncome / 30000));
  } else {
    workCredit = Math.max(0, workCreditMax - (taxableIncome - workCreditThreshold) * workCreditFactor);
  }

  const combinedTaxAndPremium = Math.max(0, baseTax - genCredit - workCredit);

  // Social security
  const ssRate = rules.tax.socialSecurity?.employeeRate;
  const ssCap = rules.tax.socialSecurity?.cap;
  if (ssRate === undefined || ssCap === undefined) {
    throw new Error("Netherlands Volksverzekeringen rate or cap is not configured in country rules");
  }
  const rawSocialSecurity = ssCap === null ? (grossIncome * ssRate) : Math.min(grossIncome, ssCap) * ssRate;
  
  const socialSecurity = Math.min(rawSocialSecurity, combinedTaxAndPremium);
  const incomeTax = Math.max(0, combinedTaxAndPremium - socialSecurity);

  const breakdown: Array<{ label: string; amount: number }> = [];
  breakdown.push({ label: "Income Tax (Box 1)", amount: baseTax - rawSocialSecurity });
  breakdown.push({ label: "Tax Credits (Applied Deductions)", amount: -(genCredit + workCredit) });
  breakdown.push({ label: "Volksverzekeringen (Social)", amount: socialSecurity });

  return { incomeTax, socialSecurity, additionalTaxes: {}, breakdown, taxableIncome, standardDeduction, marginalTaxRate };
}

function calculateFallbackTax(
  grossIncome: number,
  rules: ReturnType<typeof getCountryRules>
): { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number } {
  let standardDeduction = 0;
  let taxableIncome = grossIncome;
  
  if (rules.tax.incomeTax.personalAllowance) {
    standardDeduction = rules.tax.incomeTax.personalAllowance;
    taxableIncome = Math.max(0, grossIncome - standardDeduction);
  } else if (rules.tax.incomeTax.standardDeduction) {
    standardDeduction = rules.tax.incomeTax.standardDeduction;
    taxableIncome = Math.max(0, grossIncome - standardDeduction);
  }

  const taxCalc = calculateBracketsTax(taxableIncome, rules.tax.incomeTax.brackets || []);
  const incomeTax = taxCalc.tax;
  const marginalTaxRate = taxCalc.marginalRate;

  let socialSecurity = 0;
  if (rules.tax.socialSecurity) {
    const rate = rules.tax.socialSecurity.employeeRate;
    const cap = rules.tax.socialSecurity.cap;
    socialSecurity = cap === null ? grossIncome * rate : Math.min(grossIncome, cap) * rate;
  }

  const breakdown: Array<{ label: string; amount: number }> = [];
  breakdown.push({ label: "Income Tax", amount: incomeTax });
  if (socialSecurity > 0) {
    breakdown.push({ label: rules.tax.socialSecurity?.label || "Social Security", amount: socialSecurity });
  }

  return { incomeTax, socialSecurity, additionalTaxes: {}, breakdown, taxableIncome, standardDeduction, marginalTaxRate };
}

export function calculateIncomeTax(
  grossIncome: number,
  countryCode: string,
  options: {
    filingStatus?: string;      // US: single, married_jointly, head_of_household
    taxRegime?: "old" | "new";   // India: old, new
    deductions80C?: number;       // India old regime Section 80C-style deductions
    additionalDeductions?: number; // India old regime other eligible deductions
    stateTaxRate?: number;       // US state tax rate
    churchTax?: boolean;         // Germany: true/false
    thirtyPercentExpats?: boolean; // Netherlands: true/false
    calculateHecs?: boolean;     // Australia: true/false
  } = {}
): TaxCalculationResult {
  const rules = getCountryRules(countryCode);
  const code = countryCode.toLowerCase();
  
  let result: { incomeTax: number; socialSecurity: number; additionalTaxes: Record<string, number>; breakdown: Array<{ label: string; amount: number }>; taxableIncome: number; standardDeduction: number; marginalTaxRate: number };

  // Delegate to country-specific functions
  switch (code) {
    case "us":
      result = calculateUSTax(grossIncome, rules, options);
      break;
    case "gb":
      result = calculateUKTax(grossIncome, rules);
      break;
    case "au":
      result = calculateAUTax(grossIncome, rules, options);
      break;
    case "in":
      result = calculateINTax(grossIncome, rules, options);
      break;
    case "ca":
      result = calculateCATax(grossIncome, rules);
      break;
    case "de":
      result = calculateDETax(grossIncome, rules, options);
      break;
    case "nl":
      result = calculateNLTax(grossIncome, rules, options);
      break;
    default:
      result = calculateFallbackTax(grossIncome, rules);
      break;
  }

  // Calculate totals
  let additionalTaxesSum = 0;
  Object.values(result.additionalTaxes).forEach((val) => {
    additionalTaxesSum += val;
  });

  const totalTax = result.incomeTax + result.socialSecurity + additionalTaxesSum;
  const takeHomePay = Math.max(0, grossIncome - totalTax);
  const effectiveTaxRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;

  return {
    grossIncome,
    standardDeduction: result.standardDeduction,
    taxableIncome: result.taxableIncome,
    incomeTax: result.incomeTax,
    socialSecurity: result.socialSecurity,
    additionalTaxes: result.additionalTaxes,
    totalTax,
    takeHomePay,
    effectiveTaxRate,
    marginalTaxRate: result.marginalTaxRate * 100,
    breakdown: result.breakdown,
  };
}

/**
 * Capital Gains Tax Engine
 */
export function calculateCapitalGains(
  gain: number,
  holdingPeriodYears: number,
  countryCode: string
): { gain: number; taxOwed: number; netProceeds: number; taxRate: number } {
  const code = countryCode.toLowerCase();
  
  if (code === "sg") {
    // Singapore has zero capital gains tax!
    return { gain, taxOwed: 0, netProceeds: gain, taxRate: 0 };
  }

  const rules = getCountryRules(countryCode);
  let taxOwed = 0;
  let rate = rules.tax.countrySpecific?.capitalGainsRate ?? 0.15; // default fallback rate

  if (code === "us") {
    if (holdingPeriodYears < 1) {
      const stBrackets = rules.tax.capitalGains?.shortTerm.rates || [];
      rate = stBrackets[0]?.rate ?? 0.22;
      taxOwed = gain * rate;
    } else {
      const ltBrackets = rules.tax.capitalGains?.longTerm.rates || [];
      const calc = calculateBracketsTax(gain, ltBrackets);
      taxOwed = calc.tax;
      rate = calc.marginalRate;
    }
  } 
  
  else if (code === "au") {
    // Australia offers 50% discount for assets held > 1 year
    const discountFactor = holdingPeriodYears >= 1 
      ? (rules.tax.countrySpecific?.cgtDiscountFactor ?? 0.50) 
      : 1.0;
    const averageRate = rules.tax.countrySpecific?.averageMarginalRate ?? 0.325;
    rate = averageRate * discountFactor;
    taxOwed = gain * rate;
  } 
  
  else if (code === "gb") {
    // UK basic rate vs higher rate CGT
    const brackets = rules.tax.capitalGains?.shortTerm?.rates || [];
    if (brackets.length > 0) {
      const calc = calculateBracketsTax(gain, brackets);
      taxOwed = calc.tax;
      rate = calc.marginalRate;
    } else {
      rate = gain > 50000 ? 0.20 : 0.10;
      taxOwed = gain * rate;
    }
  } 
  
  else {
    // General fallback progressive brackets if available, or flat rate
    const brackets = rules.tax.capitalGains?.shortTerm?.rates || rules.tax.incomeTax.brackets || [];
    if (brackets.length > 0) {
      const calc = calculateBracketsTax(gain, brackets);
      taxOwed = calc.tax;
      rate = calc.marginalRate;
    } else {
      taxOwed = gain * rate;
    }
  }

  const netProceeds = gain - taxOwed;

  return {
    gain,
    taxOwed,
    netProceeds,
    taxRate: rate * 100,
  };
}

/**
 * Self Employment Tax Engine
 */
export function calculateSelfEmploymentTax(
  income: number,
  countryCode: string
): { netProfit: number; seTax: number; incomeTax: number; totalTax: number; takeHome: number } {
  const rules = getCountryRules(countryCode);
  const code = countryCode.toLowerCase();

  let seTax = 0;
  let incomeTax = 0;

  if (code === "us") {
    // US Self-Employment Tax
    const factor = rules.tax.selfEmployment?.seEarningsFactor ?? 0.9235;
    const ssRate = rules.tax.selfEmployment?.seSocialSecurityRate ?? 0.124;
    const medRate = rules.tax.selfEmployment?.seMedicareRate ?? 0.029;
    const seEarnings = income * factor;
    const ssCap = rules.tax.socialSecurity?.cap || 176100;
    const ssTax = Math.min(seEarnings, ssCap) * ssRate;
    const medTax = seEarnings * medRate;
    seTax = ssTax + medTax;
    
    // Half of SE tax is tax-deductible from gross income before normal income tax
    const adjustedIncome = Math.max(0, income - seTax / 2);
    const taxResult = calculateIncomeTax(adjustedIncome, "us", { filingStatus: "single" });
    incomeTax = taxResult.incomeTax;
  } else {
    // Fallback - calculate standard tax
    const taxResult = calculateIncomeTax(income, countryCode);
    incomeTax = taxResult.incomeTax;
    seTax = taxResult.socialSecurity;
  }

  const totalTax = seTax + incomeTax;
  const takeHome = Math.max(0, income - totalTax);

  return {
    netProfit: income,
    seTax,
    incomeTax,
    totalTax,
    takeHome,
  };
}

/**
 * Helper to calculate a phased-out cap for OBBBA deductions
 */
function calculatePhaseOutCap(
  magi: number,
  start: number,
  range: number,
  maxLimit: number
): number {
  if (magi <= start) return maxLimit;
  const fraction = Math.max(0, Math.min(1, (magi - start) / range));
  return maxLimit * (1 - fraction);
}

/**
 * OBBBA No Tax on Tips Deduction Engine
 */
export function calculateNoTaxOnTips(
  tipIncome: number,
  filingStatus: string,
  magi: number
): { deduction: number; taxSaved: number } {
  const maxLimit = 25000;
  const isJoint = filingStatus === "married_jointly";
  const start = isJoint ? 300000 : 150000;
  const end = isJoint ? 550000 : 400000;
  const range = end - start;

  const cap = calculatePhaseOutCap(magi, start, range, maxLimit);
  const deduction = Math.min(tipIncome, cap);

  const taxBefore = calculateIncomeTax(magi, "us", { filingStatus });
  const taxAfter = calculateIncomeTax(Math.max(0, magi - deduction), "us", { filingStatus });
  const taxSaved = Math.max(0, taxBefore.incomeTax - taxAfter.incomeTax);

  return {
    deduction: Math.round(deduction),
    taxSaved: Math.round(taxSaved)
  };
}

/**
 * OBBBA No Tax on Overtime Deduction Engine
 */
export function calculateNoTaxOnOvertime(
  overtimeIncome: number,
  filingStatus: string,
  magi: number
): { deduction: number; taxSaved: number } {
  const isJoint = filingStatus === "married_jointly";
  const maxLimit = isJoint ? 25000 : 12500;
  const start = isJoint ? 300000 : 150000;
  const end = isJoint ? 550000 : 275000;
  const range = end - start;

  const cap = calculatePhaseOutCap(magi, start, range, maxLimit);
  const deduction = Math.min(overtimeIncome, cap);

  const taxBefore = calculateIncomeTax(magi, "us", { filingStatus });
  const taxAfter = calculateIncomeTax(Math.max(0, magi - deduction), "us", { filingStatus });
  const taxSaved = Math.max(0, taxBefore.incomeTax - taxAfter.incomeTax);

  return {
    deduction: Math.round(deduction),
    taxSaved: Math.round(taxSaved)
  };
}

/**
 * OBBBA Senior Deduction Engine
 * Single/HOH: up to $6,000, phase-out $75,000–$175,000 MAGI
 * Married Filing Jointly:
 *   - One spouse 65+: up to $6,000, phase-out $150,000–$250,000 MAGI
 *   - Both spouses 65+: up to $12,000, phase-out $150,000–$250,000 MAGI
 * Reduction rate: $60 per $1,000 MAGI over threshold (= 6%/thousand)
 */
export function calculateSeniorDeduction(
  age: number,
  filingStatus: string,
  magi: number,
  spouseAge?: number
): { deduction: number; taxSaved: number } {
  const isJoint = filingStatus === "married_jointly";
  const primarySenior = age >= 65;

  if (!primarySenior) {
    return { deduction: 0, taxSaved: 0 };
  }

  // For joint: if spouse age provided and also 65+, cap doubles to $12,000
  const bothSenior = isJoint && spouseAge !== undefined && spouseAge >= 65;
  const maxLimit = isJoint ? (bothSenior ? 12000 : 6000) : 6000;

  const start = isJoint ? 150000 : 75000;
  const end = isJoint ? 250000 : 175000;
  const range = end - start;

  let cap = maxLimit;
  if (magi > start) {
    const fraction = Math.max(0, Math.min(1, (magi - start) / range));
    cap = maxLimit * (1 - fraction);
  }

  const deduction = cap;

  const taxBefore = calculateIncomeTax(magi, "us", { filingStatus });
  const taxAfter = calculateIncomeTax(Math.max(0, magi - deduction), "us", { filingStatus });
  const taxSaved = Math.max(0, taxBefore.incomeTax - taxAfter.incomeTax);

  return {
    deduction: Math.round(deduction),
    taxSaved: Math.round(taxSaved)
  };
}

/**
 * OBBBA Auto Loan Interest Deduction Engine
 */
export function calculateAutoLoanInterestDeduction(
  autoInterestPaid: number,
  magi: number,
  autoUsAssembled: string,
  filingStatus: string = "single"
): { deduction: number; taxSaved: number } {
  if (autoUsAssembled !== "yes") {
    return { deduction: 0, taxSaved: 0 };
  }

  const isJoint = filingStatus === "married_jointly";
  const maxLimit = 10000;
  const start = isJoint ? 200000 : 100000;
  const end = isJoint ? 250000 : 150000;
  const range = end - start;

  const cap = calculatePhaseOutCap(magi, start, range, maxLimit);
  const deduction = Math.min(autoInterestPaid, cap);

  const taxBefore = calculateIncomeTax(magi, "us", { filingStatus });
  const taxAfter = calculateIncomeTax(Math.max(0, magi - deduction), "us", { filingStatus });
  const taxSaved = Math.max(0, taxBefore.incomeTax - taxAfter.incomeTax);

  return {
    deduction: Math.round(deduction),
    taxSaved: Math.round(taxSaved)
  };
}

/**
 * OBBBA Combined Total Savings Engine
 */
export function calculateObbbaTotalSavings(
  filingStatus: string,
  magi: number,
  tipIncome: number,
  overtimeIncome: number,
  age: number,
  autoInterestPaid: number,
  autoUsAssembled: string,
  spouseAge?: number
): {
  tipsDeduction: number;
  overtimeDeduction: number;
  seniorDeduction: number;
  autoInterestDeduction: number;
  totalDeduction: number;
  taxSaved: number;
} {
  const tipsRes = calculateNoTaxOnTips(tipIncome, filingStatus, magi);
  const overtimeRes = calculateNoTaxOnOvertime(overtimeIncome, filingStatus, magi);
  const seniorRes = calculateSeniorDeduction(age, filingStatus, magi, spouseAge);
  const autoRes = calculateAutoLoanInterestDeduction(autoInterestPaid, magi, autoUsAssembled, filingStatus);

  const totalDeduction = tipsRes.deduction + overtimeRes.deduction + seniorRes.deduction + autoRes.deduction;

  const taxBefore = calculateIncomeTax(magi, "us", { filingStatus });
  const taxAfter = calculateIncomeTax(Math.max(0, magi - totalDeduction), "us", { filingStatus });
  const taxSaved = Math.max(0, taxBefore.incomeTax - taxAfter.incomeTax);

  return {
    tipsDeduction: tipsRes.deduction,
    overtimeDeduction: overtimeRes.deduction,
    seniorDeduction: seniorRes.deduction,
    autoInterestDeduction: autoRes.deduction,
    totalDeduction,
    taxSaved: Math.round(taxSaved)
  };
}

/**
/**
 * Australia Payday Super Impact Calculator Engine
 */
export function calculatePaydaySuperImpact(
  salary: number,
  currentBalance: number,
  years: number,
  returnRate: number,
  frequency: string
): {
  quarterlyFinal: number;
  paydayFinal: number;
  extraSavings: number;
  chartData: { labels: string[]; seriesA: number[]; seriesB: number[] };
} {
  const sgRate = 0.12;
  const superTaxRate = 0.15;
  
  // MSCB (Maximum Super Contribution Base) for FY 2026-27: $65,070 per quarter = $260,280 annually
  // Employers don't have to pay SG on earnings above this cap
  const mscbAnnual = 260280;
  const cappedSalary = Math.min(salary, mscbAnnual);
  
  const netAnnualContribution = cappedSalary * sgRate * (1 - superTaxRate);

  const yearsCount = Math.max(1, Math.round(years));
  const totalWeeks = yearsCount * 52;
  const weeklyRate = (returnRate / 100) / 52;

  let balPayday = currentBalance;
  let balQuarterly = currentBalance;
  
  // Track contributions waiting to be deposited for quarterly system
  let quarterlyPendingContribution = 0;

  const weeklyContributionPayday = netAnnualContribution / 52;
  const quarterlyContributionAmount = netAnnualContribution / 4;
  const weeklyAccrualForQuarterly = quarterlyContributionAmount / 13; // Accrue each week but don't invest

  const labels: string[] = [];
  const seriesA: number[] = []; // Quarterly
  const seriesB: number[] = []; // Payday

  for (let w = 1; w <= totalWeeks; w++) {
    // Payday system: deposit immediately and compound
    if (frequency === "weekly") {
      balPayday += weeklyContributionPayday;
    } else if (frequency === "fortnightly") {
      if (w % 2 === 0) {
        balPayday += weeklyContributionPayday * 2;
      }
    } else { // monthly
      if (w % 4 === 0) {
        balPayday += weeklyContributionPayday * 4.3333;
      }
    }
    balPayday *= (1 + weeklyRate); // Compound immediately
    
    // Quarterly system: accrue but don't compound until deposited
    quarterlyPendingContribution += weeklyAccrualForQuarterly;
    
    // Deposit quarterly super every 13 weeks (contribution sits idle until then)
    if (w % 13 === 0) {
      balQuarterly += quarterlyPendingContribution;
      quarterlyPendingContribution = 0; // Reset pending
    }
    balQuarterly *= (1 + weeklyRate); // Only the invested balance compounds

    // Record data at year ends
    if (w % 52 === 0) {
      const yearIdx = w / 52;
      labels.push(`Year ${yearIdx}`);
      seriesA.push(Math.round(balQuarterly));
      seriesB.push(Math.round(balPayday));
    }
  }

  // Handle remaining weeks if not exact multiple of 52
  if (labels.length === 0) {
    labels.push("Year 1");
    seriesA.push(Math.round(balQuarterly));
    seriesB.push(Math.round(balPayday));
  }

  return {
    quarterlyFinal: Math.round(balQuarterly),
    paydayFinal: Math.round(balPayday),
    extraSavings: Math.max(0, Math.round(balPayday - balQuarterly)),
    chartData: { labels, seriesA, seriesB }
  };
}

/**
 * Australia Payday Super Compliance Calculator Engine (Employer-facing)
 */
export function calculatePaydaySuperCompliance(
  employees: number,
  avgSalary: number,
  payCycle: string,
  businessInterestRate: number
): {
  quarterlyOutflow: number;
  cycleOutflow: number;
  workingCapitalInterestLost: number;
} {
  const sgRate = 0.12;
  const totalAnnualSuper = employees * avgSalary * sgRate;

  // Quarterly outflow is paid 4 times a year
  const quarterlyOutflow = totalAnnualSuper / 4;

  // Cycle outflow based on frequency
  let cycleOutflow = totalAnnualSuper / 12; // default monthly
  if (payCycle === "weekly") {
    cycleOutflow = totalAnnualSuper / 52;
  } else if (payCycle === "fortnightly") {
    cycleOutflow = totalAnnualSuper / 26;
  }

  // Working capital interest lost: holding cash for average 73 days under quarterly (90/2 + 28 days grace)
  const averageDaysHeldQuarterly = 73;
  const workingCapitalInterestLost = totalAnnualSuper * (averageDaysHeldQuarterly / 365) * (businessInterestRate / 100);

  return {
    quarterlyOutflow: Math.round(quarterlyOutflow),
    cycleOutflow: Math.round(cycleOutflow),
    workingCapitalInterestLost: Math.round(workingCapitalInterestLost)
  };
}

/**
 * UK Cash ISA 2027 Cap Impact Calculator Engine
 */
export function calculateCashIsa2027CapImpact(
  currentSavings: number,
  monthlyContribution: number,
  interestRate: number,
  age: number,
  taxRate: number
): {
  balanceOldRules10y: number;
  balanceNewRules10y: number;
  compoundingCost10y: number;
  balanceOldRules20y: number;
  balanceNewRules20y: number;
  compoundingCost20y: number;
  balanceOldRules30y: number;
  balanceNewRules30y: number;
  compoundingCost30y: number;
  chartData: { labels: string[]; seriesA: number[]; seriesB: number[] };
} {
  // SI 2026/1018 reg. 6: the £12,000 cap applies only when the saver is 64
  // or under at the END of the tax year. `age` is age on 5 April 2028, the
  // end of the first projected tax year; advance it for each later year.
  // The limits are ANNUAL subscriptions, never monthly contribution ceilings.
  // https://www.legislation.gov.uk/uksi/2026/1018/made/data.html
  const overallAnnualLimit = 20000;
  const cashAnnualLimitUnder65 = 12000;
  const monthlySaving = Number.isFinite(monthlyContribution) ? Math.max(0, Math.min(1_000_000, monthlyContribution)) : 0;
  const safeExistingBalance = Number.isFinite(currentSavings) ? Math.max(0, Math.min(1_000_000_000, currentSavings)) : 0;
  const safeInterestRate = Number.isFinite(interestRate) ? Math.max(0, Math.min(100, interestRate)) : 0;
  const safeAge = Number.isFinite(age) ? Math.max(18, Math.min(120, Math.floor(age))) : 18;
  const monthlyRate = (safeInterestRate / 100) / 12;
  // Finance Act 2026 s. 5 sets the 2027-28 savings rates at 22/42/47%.
  // Assume the selected rate and the current PSA remain constant in later
  // projection years; other taxable interest is not supplied to this model.
  // https://www.legislation.gov.uk/ukpga/2026/11/section/5
  const selectedTaxRate = taxRate === 22 || taxRate === 42 || taxRate === 47 ? taxRate : 22;
  const savingsTaxRate = selectedTaxRate / 100;
  const personalSavingsAllowance = selectedTaxRate === 47 ? 0 : selectedTaxRate === 42 ? 500 : 1000;

  // `currentSavings` is the existing cash ISA balance in both scenarios.
  let balA_isa = safeExistingBalance;
  let balA_taxable = 0;
  let balB_isa = safeExistingBalance;
  let balB_taxable = 0;
  let cashSubscriptionsAThisYear = 0;
  let cashSubscriptionsBThisYear = 0;
  let grossTaxableInterestAThisYear = 0;
  let grossTaxableInterestBThisYear = 0;

  const labels: string[] = [];
  const seriesA: number[] = [];
  const seriesB: number[] = [];

  let balanceOldRules10y = 0;
  let balanceNewRules10y = 0;
  let balanceOldRules20y = 0;
  let balanceNewRules20y = 0;
  let balanceOldRules30y = 0;
  let balanceNewRules30y = 0;

  for (let m = 1; m <= 360; m++) {
    const yearIndex = Math.floor((m - 1) / 12);
    if ((m - 1) % 12 === 0) {
      cashSubscriptionsAThisYear = 0;
      cashSubscriptionsBThisYear = 0;
      grossTaxableInterestAThisYear = 0;
      grossTaxableInterestBThisYear = 0;
    }
    const ageAtTaxYearEnd = safeAge + yearIndex;
    const cashAnnualLimit = ageAtTaxYearEnd >= 65 ? overallAnnualLimit : cashAnnualLimitUnder65;
    // Keep the full entered saving stream in BOTH paths. Amounts above the
    // overall annual ISA allowance stay taxable under either set of rules.
    const depositA_isa = Math.min(monthlySaving, Math.max(0, overallAnnualLimit - cashSubscriptionsAThisYear));
    const depositB_isa = Math.min(monthlySaving, Math.max(0, cashAnnualLimit - cashSubscriptionsBThisYear));
    cashSubscriptionsAThisYear += depositA_isa;
    cashSubscriptionsBThisYear += depositB_isa;
    const depositA_taxable = monthlySaving - depositA_isa;
    const depositB_taxable = monthlySaving - depositB_isa;

    // Scenario A: counterfactual full £20,000 annual cash ISA access.
    balA_isa = balA_isa * (1 + monthlyRate) + depositA_isa;
    const interestA = balA_taxable * monthlyRate;
    const previouslyTaxableA = Math.max(0, grossTaxableInterestAThisYear - personalSavingsAllowance);
    grossTaxableInterestAThisYear += interestA;
    const newlyTaxableA = Math.max(0, grossTaxableInterestAThisYear - personalSavingsAllowance) - previouslyTaxableA;
    balA_taxable += interestA - newlyTaxableA * savingsTaxRate + depositA_taxable;

    // Scenario B: enacted cap, with unused cash savings modelled outside an
    // ISA by user choice. Investing any remainder is optional, not assumed.
    balB_isa = balB_isa * (1 + monthlyRate) + depositB_isa;
    const interestTaxable = balB_taxable * monthlyRate;
    const previouslyTaxable = Math.max(0, grossTaxableInterestBThisYear - personalSavingsAllowance);
    grossTaxableInterestBThisYear += interestTaxable;
    const newlyTaxable = Math.max(0, grossTaxableInterestBThisYear - personalSavingsAllowance) - previouslyTaxable;
    const taxOnInterest = newlyTaxable * savingsTaxRate;
    balB_taxable = balB_taxable + interestTaxable - taxOnInterest + depositB_taxable;

    const totalA = balA_isa + balA_taxable;
    const totalB = balB_isa + balB_taxable;

    // Capture milestones
    if (m === 120) {
      balanceOldRules10y = Math.round(totalA);
      balanceNewRules10y = Math.round(totalB);
    } else if (m === 240) {
      balanceOldRules20y = Math.round(totalA);
      balanceNewRules20y = Math.round(totalB);
    } else if (m === 360) {
      balanceOldRules30y = Math.round(totalA);
      balanceNewRules30y = Math.round(totalB);
    }

    // Capture chart data at year ends
    if (m % 12 === 0) {
      const year = m / 12;
      labels.push(`Year ${year}`);
      seriesA.push(Math.round(totalA));
      seriesB.push(Math.round(totalB));
    }
  }

  return {
    balanceOldRules10y,
    balanceNewRules10y,
    compoundingCost10y: Math.max(0, balanceOldRules10y - balanceNewRules10y),
    balanceOldRules20y,
    balanceNewRules20y,
    compoundingCost20y: Math.max(0, balanceOldRules20y - balanceNewRules20y),
    balanceOldRules30y,
    balanceNewRules30y,
    compoundingCost30y: Math.max(0, balanceOldRules30y - balanceNewRules30y),
    chartData: { labels, seriesA, seriesB }
  };
}

/**
 * Netherlands Box 3 Wealth Tax Calculator Engine (2026 vs 2028 proposed)
 */
export function calculateBox3WealthTax(
  savings: number,
  investments: number,
  debts: number,
  hasPartner: boolean,
  actualSavingsReturn: number,
  actualInvestmentReturn: number,
  actualDebtInterest: number
): {
  taxableWealth2026: number;
  totalDeemedReturn2026: number;
  tax2026: number;
  actualReturn2028: number;
  taxableReturn2028: number;
  tax2028: number;
  taxSavings: number;
} {
  // --- Scenario A: 2026 System ---
  const allowance = hasPartner ? 118714 : 59357;
  const debtThreshold = hasPartner ? 7600 : 3800;

  const rateSavings = 0.0128;
  const rateInvestments = 0.0600;
  const rateDebts = 0.0270;
  const taxRate = 0.36;

  const deductibleDebts = Math.max(0, debts - debtThreshold);
  const netAssets = savings + investments - deductibleDebts;

  const deemedSavings = savings * rateSavings;
  const deemedInvestments = investments * rateInvestments;
  const deemedDebts = deductibleDebts * rateDebts;
  const totalDeemedReturn = deemedSavings + deemedInvestments - deemedDebts;

  let taxableWealth2026 = 0;
  let tax2026 = 0;

  if (netAssets > allowance) {
    taxableWealth2026 = netAssets - allowance;
    const ratio = taxableWealth2026 / netAssets;
    const calculatedIncome = totalDeemedReturn * ratio;
    tax2026 = Math.max(0, calculatedIncome * taxRate);
  }

  // --- Scenario B: 2028 Proposed System ---
  // Under 2028 law: Only debt interest is deductible, NOT brokerage fees or advisory costs
  const taxFreeResult = hasPartner ? 3600 : 1800;
  const actualReturn = actualSavingsReturn + actualInvestmentReturn - actualDebtInterest;
  const taxableReturn = Math.max(0, actualReturn - taxFreeResult);
  const tax2028 = taxableReturn * taxRate;

  return {
    taxableWealth2026: Math.round(taxableWealth2026),
    totalDeemedReturn2026: Math.round(totalDeemedReturn),
    tax2026: Math.round(tax2026),
    actualReturn2028: Math.round(actualReturn),
    taxableReturn2028: Math.round(taxableReturn),
    tax2028: Math.round(tax2028),
    taxSavings: Math.round(tax2026 - tax2028)
  };
}

/**
 * Germany Aktivrente (Active Pension) Tax Savings Engine
 * For working pensioners after reaching statutory retirement age.
 * Up to €2,000/month (€24,000/year) is tax-free on top of baseline pension.
 */
export function calculateAktivrenteTaxSavings(
  pensionIncome: number,
  monthlyActiveEarnings: number,
  churchTax: boolean
) {
  const annualActiveEarnings = monthlyActiveEarnings * 12;
  const taxFreeActiveEarnings = Math.min(annualActiveEarnings, 24000);
  const taxableActiveEarnings = Math.max(0, annualActiveEarnings - taxFreeActiveEarnings);

  // Scenario A: Without Aktivrente (all active earnings taxed)
  const totalIncomeWithout = pensionIncome + annualActiveEarnings;
  const taxWithout = calculateIncomeTax(totalIncomeWithout, "de", { churchTax });

  // Scenario B: With Aktivrente (first €24,000 of active earnings exempt)
  const totalIncomeWith = pensionIncome + taxableActiveEarnings;
  const taxWith = calculateIncomeTax(totalIncomeWith, "de", { churchTax });

  const taxWithoutTotal = taxWithout.incomeTax + (taxWithout.additionalTaxes?.["Solidaritätszuschlag"] ?? 0) + (taxWithout.additionalTaxes?.["Kirchensteuer"] ?? 0);
  const taxWithTotal = taxWith.incomeTax + (taxWith.additionalTaxes?.["Solidaritätszuschlag"] ?? 0) + (taxWith.additionalTaxes?.["Kirchensteuer"] ?? 0);
  const taxSavings = Math.max(0, taxWithoutTotal - taxWithTotal);

  // Social security on active earnings remains payable (approx. 20.3% employee share in 2026)
  const ssRate = 0.203; 
  const ssPaid = annualActiveEarnings * ssRate;

  // Social security on pension remains unaffected (health & nursing care)
  const pensionSsRate = 0.11; // ~11% employee contribution on pensions
  const pensionSsPaid = pensionIncome * pensionSsRate;

  return {
    grossPension: Math.round(pensionIncome),
    grossActiveEarnings: Math.round(annualActiveEarnings),
    taxFreeActiveEarnings: Math.round(taxFreeActiveEarnings),
    taxableActiveEarnings: Math.round(taxableActiveEarnings),
    taxWithoutTotal: Math.round(taxWithoutTotal),
    taxWithTotal: Math.round(taxWithTotal),
    taxSavings: Math.round(taxSavings),
    socialSecurityPaid: Math.round(ssPaid + pensionSsPaid),
    netIncomeWithout: Math.round(totalIncomeWithout - taxWithoutTotal - (totalIncomeWithout * ssRate)), // rough estimation for legacy comparison
    netIncomeWith: Math.round((pensionIncome - pensionSsPaid) + (annualActiveEarnings - ssPaid) - taxWithTotal)
  };
}

/**
 * Germany Frühstart-Rente (Early Start Pension) Growth Projector
 * State deposits €10/month starting at child's age (6-17) until 18.
 * Value compounded monthly, then grows without new deposits until ages 30, 50, 67.
 */
export function calculateFruehstartRenteGrowth(
  childAge: number,
  assumedReturn: number
) {
  const monthlyContribution = 10;
  const yearsTo18 = Math.max(0, 18 - childAge);
  const monthsTo18 = yearsTo18 * 12;
  const monthlyRate = assumedReturn / 100 / 12;

  let valueAt18 = 0;
  if (monthsTo18 > 0) {
    if (monthlyRate === 0) {
      valueAt18 = monthsTo18 * monthlyContribution;
    } else {
      valueAt18 = monthlyContribution * ((Math.pow(1 + monthlyRate, monthsTo18) - 1) / monthlyRate);
    }
  }

  const compoundAtAge = (targetAge: number) => {
    const yearsAfter18 = Math.max(0, targetAge - 18);
    return valueAt18 * Math.pow(1 + assumedReturn / 100, yearsAfter18);
  };

  const totalContributions = monthsTo18 * monthlyContribution;
  const valueAt30 = compoundAtAge(30);
  const valueAt50 = compoundAtAge(50);
  const valueAt67 = compoundAtAge(67);

  return {
    childAge,
    yearsOfContribution: yearsTo18,
    totalContributions: Math.round(totalContributions),
    valueAt18: Math.round(valueAt18),
    valueAt30: Math.round(valueAt30),
    valueAt50: Math.round(valueAt50),
    valueAt67: Math.round(valueAt67),
    totalInterestAt67: Math.round(valueAt67 - totalContributions),
    growthMultiplier: totalContributions > 0 ? parseFloat((valueAt67 / totalContributions).toFixed(1)) : 1.0
  };
}

export interface UpsPensionResult {
  yearsOfService: number;
  lastBasicPay: number;
  daRate: number;
  monthlyEmoluments: number;
  isEligible: boolean;
  guaranteedPension: number;
  familyPension: number;
  lumpSum: number;
}

export function calculateUpsPension(
  yearsOfService: number,
  lastBasicPay: number,
  daRate: number
): UpsPensionResult {
  const isEligible = yearsOfService >= 10;
  
  let guaranteedPension = 0;
  let familyPension = 0;
  let lumpSum = 0;

  if (isEligible) {
    const serviceFactor = Math.min(25, yearsOfService) / 25;
    const basePension = 0.5 * lastBasicPay * serviceFactor;
    
    guaranteedPension = Math.max(10000, basePension);
    familyPension = guaranteedPension * 0.6;
    
    const completedHalfYears = Math.floor(yearsOfService * 2);
    const monthlyEmoluments = lastBasicPay * (1 + daRate / 100);
    lumpSum = 0.1 * monthlyEmoluments * completedHalfYears;
  }

  return {
    yearsOfService,
    lastBasicPay,
    daRate,
    monthlyEmoluments: Math.round(lastBasicPay * (1 + daRate / 100)),
    isEligible,
    guaranteedPension: Math.round(guaranteedPension),
    familyPension: Math.round(familyPension),
    lumpSum: Math.round(lumpSum),
  };
}

export interface Pillar3aRetroactiveResult {
  gapYears: number;
  annualLimit: number;
  totalGapVal: number;
  annualTaxSavings: number;
  totalTaxSavings: number;
  timeToClose: number;
  pensionGrowth10Yr: number;
  pensionGrowth20Yr: number;
  pensionGrowth30Yr: number;
  taxSavingsGrowth10Yr: number;
  taxSavingsGrowth20Yr: number;
  taxSavingsGrowth30Yr: number;
}

export function calculatePillar3aRetroactiveBuyIn(
  employmentType: string,
  taxableIncome: number,
  gapYears: number,
  marginalTaxRate: number,
  expectedReturn: number = 6.0
): Pillar3aRetroactiveResult {
  const isSalaried = employmentType === "salaried" || employmentType === "Salaried" || employmentType === "yes";
  const annualLimit = isSalaried ? 7258 : Math.min(36288, 0.20 * taxableIncome);
  
  const totalGapVal = gapYears * annualLimit;
  const annualTaxSavings = annualLimit * (marginalTaxRate / 100);
  const totalTaxSavings = totalGapVal * (marginalTaxRate / 100);
  const timeToClose = gapYears;

  const rate = expectedReturn / 100;
  
  const compoundTaxSavings = (years: number) => {
    return totalTaxSavings * Math.pow(1 + rate, years);
  };

  const compoundPension = (years: number) => {
    return totalGapVal * Math.pow(1 + rate, years);
  };

  return {
    gapYears,
    annualLimit: Math.round(annualLimit),
    totalGapVal: Math.round(totalGapVal),
    annualTaxSavings: Math.round(annualTaxSavings),
    totalTaxSavings: Math.round(totalTaxSavings),
    timeToClose,
    pensionGrowth10Yr: Math.round(compoundPension(10)),
    pensionGrowth20Yr: Math.round(compoundPension(20)),
    pensionGrowth30Yr: Math.round(compoundPension(30)),
    taxSavingsGrowth10Yr: Math.round(compoundTaxSavings(10)),
    taxSavingsGrowth20Yr: Math.round(compoundTaxSavings(20)),
    taxSavingsGrowth30Yr: Math.round(compoundTaxSavings(30)),
  };
}

/**
 * OBBBA Section 530A Children's Investment Account (Trump Account) Growth Engine
 */
export function calculateTrumpAccountGrowth(
  childBirthYear: number = 2026,
  includeFederalSeed: string = "yes",
  employerContribution: number = 0,
  annualContribution: number = 2000,
  annualReturn: number = 7.5,
  retirementAge: number = 65,
  currentAge: number = 0
) {
  const isSeedEligible = childBirthYear >= 2025 && childBirthYear <= 2028;
  const hasSeed = includeFederalSeed === "yes" && isSeedEligible;
  const seedAmount = hasSeed ? 1000 : 0;

  const effEmployer = Math.max(0, Math.min(2500, employerContribution));
  const effAnnual = Math.max(0, Math.min(5000 - effEmployer, annualContribution));
  const totalAnnual = effAnnual + effEmployer;

  const returnRate = annualReturn / 100;

  let balance = seedAmount;
  let accumulatedBasis = 0;
  let accumulatedEmployer = 0;
  let accumulatedSeed = seedAmount;
  let cumulativeInterest = 0;

  let balanceAt18 = 0;
  let balanceAt27 = 0;
  let balanceAt55 = 0;
  let balanceAt65 = 0;

  const startAge = Math.max(0, Math.min(18, currentAge));

  // Add initial contribution if starting before age 18 (contributions made at start of year)
  if (startAge < 18) {
    accumulatedBasis += effAnnual;
    accumulatedEmployer += effEmployer;
    balance += totalAnnual;
  }

  const projection: Array<{
    age: number;
    contributions: number;
    interest: number;
    savings: number;
  }> = [];
  
  projection.push({
    age: startAge,
    contributions: Math.round(accumulatedBasis + accumulatedEmployer + accumulatedSeed),
    interest: Math.round(cumulativeInterest),
    savings: Math.round(balance)
  });

  const maxAge = Math.max(65, retirementAge);

  for (let age = startAge + 1; age <= maxAge; age++) {
    // 1. Apply growth from previous year
    const growth = balance * returnRate;
    cumulativeInterest += growth;
    balance += growth;

    // 2. Add contribution at start of year (only for ages < 18)
    if (age < 18) {
      accumulatedBasis += effAnnual;
      accumulatedEmployer += effEmployer;
      balance += totalAnnual;
    }

    // Capture milestones
    if (age === 18) balanceAt18 = balance;
    if (age === 27) balanceAt27 = balance;
    if (age === 55) balanceAt55 = balance;
    if (age === 65) balanceAt65 = balance;

    // Only add to user-facing projection array if within retirementAge bounds
    if (age <= retirementAge) {
      projection.push({
        age,
        contributions: Math.round(accumulatedBasis + accumulatedEmployer + accumulatedSeed),
        interest: Math.round(cumulativeInterest),
        savings: Math.round(balance)
      });
    }
  }

  // Handle case where child starts past milestones (fallback)
  if (startAge >= 18) balanceAt18 = balanceAt18 || balance;
  if (startAge >= 27) balanceAt27 = balanceAt27 || balance;
  if (startAge >= 55) balanceAt55 = balanceAt55 || balance;
  if (startAge >= 65) balanceAt65 = balanceAt65 || balance;

  const endBalance = projection[projection.length - 1].savings;
  // Calculate basis based on final year of user's requested projection
  const endProj = projection[projection.length - 1];
  const totalContributions = accumulatedBasis + accumulatedEmployer + accumulatedSeed;
  const totalBasis = accumulatedBasis;
  const totalTaxable = Math.max(0, endBalance - totalBasis);

  const taxFreePercent = endBalance > 0 ? (totalBasis / endBalance) * 100 : 0;
  const taxablePercent = endBalance > 0 ? (totalTaxable / endBalance) * 100 : 0;

  return {
    endBalance: Math.round(endBalance),
    totalContributions: Math.round(totalContributions),
    totalBasis: Math.round(totalBasis),
    totalTaxable: Math.round(totalTaxable),
    taxFreePercent: parseFloat(taxFreePercent.toFixed(1)),
    taxablePercent: parseFloat(taxablePercent.toFixed(1)),
    balanceAt18: Math.round(balanceAt18),
    balanceAt27: Math.round(balanceAt27),
    balanceAt55: Math.round(balanceAt55),
    balanceAt65: Math.round(balanceAt65),
    projection
  };
}

/**
 * NZ Progressive Income Tax Calculator for 2025/2026
 */
function calculateNZIncomeTax(income: number): number {
  let tax = 0;
  if (income <= 15600) {
    tax = income * 0.105;
  } else if (income <= 53500) {
    tax = 15600 * 0.105 + (income - 15600) * 0.175;
  } else if (income <= 78100) {
    tax = 15600 * 0.105 + (53500 - 15600) * 0.175 + (income - 53500) * 0.30;
  } else if (income <= 180000) {
    tax = 15600 * 0.105 + (53500 - 15600) * 0.175 + (78100 - 53500) * 0.30 + (income - 78100) * 0.33;
  } else {
    tax = 15600 * 0.105 + (53500 - 15600) * 0.175 + (78100 - 53500) * 0.30 + (180000 - 78100) * 0.33 + (income - 180000) * 0.39;
  }
  return tax;
}

/**
 * NZ ESCT (Employer Superannuation Contribution Tax) Rate
 */
function getNZESCTRate(income: number): number {
  // IRD 2026-27 ESCT bands; the assessment basis includes salary plus gross employer super.
  if (income <= 18720) return 0.105;
  if (income <= 64200) return 0.175;
  if (income <= 93720) return 0.30;
  if (income <= 216000) return 0.33;
  return 0.39;
}

/**
 * Compulsory KiwiSaver Paycheck and Balance Impact Engine
 */
export function calculateCompulsoryKiwiSaverImpact(
  grossSalary: number = 75000,
  currentVoluntaryRate: number = 3.5,
  proposedScenario: string = "gradual-8",
  remunerationType: string = "added",
  annualReturn: number = 6.0,
  termYears: number = 30,
  currentBalance: number = 15000,
  currentAge: number = 35
) {
  const salary = Number.isFinite(grossSalary) ? Math.max(0, Math.min(1_000_000_000, grossSalary)) : 0;
  const currentRate = [0, 3.5, 4, 6, 8, 10].includes(currentVoluntaryRate) ? currentVoluntaryRate : 3.5;
  const safeReturn = Number.isFinite(annualReturn) ? Math.max(0, Math.min(15, annualReturn)) : 0;
  const years = Number.isFinite(termYears) ? Math.max(1, Math.min(50, Math.floor(termYears))) : 1;
  const age = Number.isFinite(currentAge) ? Math.max(16, Math.min(64, Math.floor(currentAge))) : 35;
  const startingBalance = Number.isFinite(currentBalance) ? Math.max(0, Math.min(1_000_000_000, currentBalance)) : 0;
  const fixedPackage = remunerationType === "deducted";

  // 1. Current voluntary calculation
  // Zero models a non-contributing employee; the standard employer contribution then stops.
  const curEmpRate = currentRate > 0 ? 3.5 : 0;
  let curBaseSalary = salary;
  
  if (fixedPackage) {
    // A fixed all-in package is split into cash salary and employer contribution.
    // The employer payment remains on top of the resulting agreed gross salary.
    curBaseSalary = salary / (1 + curEmpRate / 100);
  }
  
  const curEmployeeContrib = curBaseSalary * (currentRate / 100);
  const curEmployerGross = curBaseSalary * (curEmpRate / 100);
  const curESCTRate = getNZESCTRate(curBaseSalary + curEmployerGross);
  const curEmployerNet = curEmployerGross * (1 - curESCTRate);
  
  // Government Match: 25c per dollar up to $260.72
  // Salary is used as a taxable-income proxy; other income/age/residence are not inputs.
  const curGovMatch = curBaseSalary <= 180000 ? Math.min(260.72, curEmployeeContrib * 0.25) : 0;
  
  const curTax = calculateNZIncomeTax(curBaseSalary);
  // ACC Levy 2026/2027: 1.75% capped at $156,641
  const curACC = Math.min(156641, curBaseSalary) * 0.0175;
  const curPaycheck = Math.max(0, curBaseSalary - curTax - curACC - curEmployeeContrib);

  // 2. Proposed compulsory scenario parsing
  let propEmpRate = 5;
  let propEmployerRate = 5;
  if (proposedScenario === "gradual-8") {
    propEmpRate = 4;
    propEmployerRate = 4;
  } else if (proposedScenario === "gradual-12") {
    propEmpRate = 6;
    propEmployerRate = 6;
  } else if (proposedScenario !== "gradual-10") {
    propEmpRate = 4;
    propEmployerRate = 4;
  }
  
  // The selected comparison assumes participation; 5% and 6% are not law.
  const effEmployeeRate = Math.max(currentRate, propEmpRate);
  const effEmployerRate = propEmployerRate;
  
  let propBaseSalary = salary;
  if (fixedPackage) {
    propBaseSalary = salary / (1 + effEmployerRate / 100);
  }
  
  const propEmployeeContrib = propBaseSalary * (effEmployeeRate / 100);
  const propEmployerGross = propBaseSalary * (effEmployerRate / 100);
  const propESCTRate = getNZESCTRate(propBaseSalary + propEmployerGross);
  const propEmployerNet = propEmployerGross * (1 - propESCTRate);
  const propGovMatch = propBaseSalary <= 180000 ? Math.min(260.72, propEmployeeContrib * 0.25) : 0;
  
  const propTax = calculateNZIncomeTax(propBaseSalary);
  const propACC = Math.min(156641, propBaseSalary) * 0.0175;
  const propPaycheck = Math.max(0, propBaseSalary - propTax - propACC - propEmployeeContrib);

  // 3. Projections over time
  const rate = safeReturn / 100;
  
  let curBalance = startingBalance;
  let propBalance = startingBalance;
  
  let curAccumulatedEmployer = 0;

  let propAccumulatedContributions = 0;
  let propAccumulatedInterest = 0;
  let propAccumulatedEmployer = 0;

  const projection: Array<{
    age: number;
    contributions: number;
    interest: number;
    savings: number;
  }> = [];

  // Year 0 is the balance at the entered age, before projected deposits.
  projection.push({
    age: 0,
    contributions: Math.round(startingBalance),
    interest: 0,
    savings: Math.round(startingBalance)
  });

  // Statutory employer payments and the government contribution are not
  // projected past age 65. This scenario also stops employee deposits then;
  // a member can choose to keep making voluntary deposits separately.
  const contributionYears = Math.min(years, 65 - age);
  for (let yr = 1; yr <= years; yr++) {
    const contributingThisYear = yr <= contributionYears;
    // Current growth
    const curGrowth = curBalance * rate;
    curBalance += curGrowth;
    if (contributingThisYear) {
      curBalance += curEmployeeContrib + curEmployerNet + curGovMatch;
      curAccumulatedEmployer += curEmployerNet;
    }

    // Proposed growth
    const propGrowth = propBalance * rate;
    propAccumulatedInterest += propGrowth;
    propBalance += propGrowth;
    if (contributingThisYear) {
      propBalance += propEmployeeContrib + propEmployerNet + propGovMatch;
      propAccumulatedContributions += propEmployeeContrib;
      propAccumulatedEmployer += propEmployerNet;
    }

    // Selected comparison rate: enacted 2028 for members or a hypothetical higher rate.
    projection.push({
      age: yr,
      contributions: Math.round(startingBalance + propAccumulatedContributions + propAccumulatedEmployer + (propGovMatch * Math.min(yr, contributionYears))),
      interest: Math.round(propAccumulatedInterest),
      savings: Math.round(propBalance)
    });
  }

  const paycheckImpact = propPaycheck - curPaycheck;
  const balanceDifference = propBalance - curBalance;

  return {
    // Paycheck details
    currentPaycheck: Math.round(curPaycheck),
    proposedPaycheck: Math.round(propPaycheck),
    paycheckImpact: Math.round(paycheckImpact),
    
    // Balance details
    currentFinalBalance: Math.round(curBalance),
    proposedFinalBalance: Math.round(propBalance),
    balanceDifference: Math.round(balanceDifference),
    
    // Employer accumulation
    totalAccumulatedEmployerCurrent: Math.round(curAccumulatedEmployer),
    totalAccumulatedEmployerProposed: Math.round(propAccumulatedEmployer),
    projectedContributionYears: contributionYears,
    
    // Standard output names mapping
    endBalance: Math.round(propBalance),
    totalContributions: Math.round(startingBalance + propAccumulatedContributions + propAccumulatedEmployer + (propGovMatch * contributionYears)),
    totalInterest: Math.round(propAccumulatedInterest),
    
    useYearLabel: true,
    projection
  };
}

/**
 * Canada New Home GST Rebate Calculator (Bill C-4, 2026 Budget)
 */
export function calculateCanadaNewHomeGstRebate(
  homePrice: number = 850000,
  isFirstTimeBuyer: string = "yes",
  hbpAmount: number = 60000,
  marginalTaxRate: number = 30,
  province: string = "other"
) {
  const cents = (amount: number) => Math.round(amount * 100) / 100;
  const price = Number.isFinite(homePrice) ? Math.max(0, Math.min(1_000_000_000_000, homePrice)) : 0;
  const withdrawal = Number.isFinite(hbpAmount) ? cents(Math.max(0, Math.min(60_000, hbpAmount))) : 0;
  const rate = Number.isFinite(marginalTaxRate) ? Math.max(0, Math.min(54, marginalTaxRate)) : 0;
  const isFthb = isFirstTimeBuyer === "yes";
  const isOntario = province === "on";

  const relief = calculateCanadaHomeRebates(price, isFthb, isOntario);
  const standardTax = cents(price * (isOntario ? 0.13 : 0.05));
  const totalRebate = relief.totalRebate;
  const netTaxPaid = cents(Math.max(0, standardTax - totalRebate));
  // An HBP withdrawal is down-payment liquidity, not an additional tax rebate.
  // This comparison to an ordinary taxable RRSP withdrawal is a tax deferral.
  const hbpTaxSavings = cents(withdrawal * (rate / 100));
  const totalSavings = totalRebate;

  const projection: Array<{
    year: number;
    annualRepayment: number;
    totalRepaid: number;
    remainingBalance: number;
  }> = [];

  const annualRepay = cents(withdrawal / 15);
  let totalRepaid = 0;
  let remainingBalance = withdrawal;

  projection.push({
    year: 0,
    annualRepayment: 0,
    totalRepaid: 0,
    remainingBalance: cents(remainingBalance)
  });

  // For a first withdrawal in 2026, CRA says 2031 is the first repayment
  // year: year 5 after withdrawal. The 15th repayment is in year 19.
  // https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans/what-home-buyers-plan.html
  for (let yr = 1; yr <= 19; yr++) {
    let currentRepay = 0;
    if (yr >= 5) {
      currentRepay = yr === 19 ? remainingBalance : Math.min(annualRepay, remainingBalance);
      totalRepaid = cents(totalRepaid + currentRepay);
      remainingBalance = cents(remainingBalance - currentRepay);
    }

    projection.push({
      year: yr,
      annualRepayment: cents(currentRepay),
      totalRepaid: cents(totalRepaid),
      remainingBalance: cents(Math.max(0, remainingBalance))
    });
  }

  return {
    standardGst: standardTax,
    gstRebate: relief.federalRebate,
    federalRebate: relief.federalRebate,
    enhancedOntario: relief.enhancedOntario,
    ontarioTopUp: relief.ontarioTopUp,
    netGstPaid: netTaxPaid,
    hbpTaxSavings,
    totalSavings,
    endBalance: totalSavings, // mapping for layout card
    useHbpRepaymentSchedule: true,
    projection
  };
}

export function calculateIrsPenaltyReliefAep(
  unpaidTax: number = 5000,
  monthsLate: number = 3,
  penaltyType: string = "both",
  hasCleanHistory: string = "yes",
  depositDaysLate: number = 10
) {
  const isAepEligible = hasCleanHistory === "yes" && penaltyType !== "deposit";

  // Standard Failure to File (FTF) Penalty
  let ftf = 0;
  if (penaltyType === "file" || penaltyType === "both") {
    // 5% per month, capped at 25%. If both apply, reduce by FTP (so 4.5%)
    const rate = penaltyType === "both" ? 0.045 : 0.05;
    ftf = unpaidTax * rate * Math.min(5, monthsLate);
    
    // Minimum penalty if over 60 days late (strictly > 60 days, starting at Month 3)
    if (monthsLate >= 3) {
      const minPenalty = Math.min(unpaidTax, 525); // 2026 minimum penalty (returns due after Dec 31, 2025)
      if (ftf < minPenalty) ftf = minPenalty;
    }
  }

  // Standard Failure to Pay (FTP) Penalty
  let ftp = 0;
  if (penaltyType === "pay" || penaltyType === "both") {
    // 0.5% per month, capped at 25% (which takes 50 months)
    ftp = unpaidTax * 0.005 * Math.min(50, monthsLate);
  }

  // Standard Failure to Deposit (FTD) Penalty
  let ftd = 0;
  if (penaltyType === "deposit") {
    let rate = 0.10;
    if (depositDaysLate <= 5) rate = 0.02;
    else if (depositDaysLate <= 15) rate = 0.05;
    ftd = unpaidTax * rate;
  }

  const standardPenalty = Math.round(ftf + ftp + ftd);
  const reliefAmount = isAepEligible ? standardPenalty : 0;
  const netPenaltyOwed = Math.round(standardPenalty - reliefAmount);

  // Generate monthly projection
  const projection: Array<{
    month: number;
    standardPenalty: number;
    aepRelief: number;
    netOwed: number;
  }> = [];

  const maxMonths = Math.max(1, monthsLate);

  projection.push({
    month: 0,
    standardPenalty: 0,
    aepRelief: 0,
    netOwed: 0
  });

  for (let m = 1; m <= maxMonths; m++) {
    let currentFtf = 0;
    if (penaltyType === "file" || penaltyType === "both") {
      const rate = penaltyType === "both" ? 0.045 : 0.05;
      currentFtf = unpaidTax * rate * Math.min(5, m);
      if (m >= 3) {
        const minPenalty = Math.min(unpaidTax, 525); // 2026 minimum penalty
        if (currentFtf < minPenalty) currentFtf = minPenalty;
      }
    }

    let currentFtp = 0;
    if (penaltyType === "pay" || penaltyType === "both") {
      currentFtp = unpaidTax * 0.005 * Math.min(50, m);
    }

    let currentFtd = 0;
    if (penaltyType === "deposit") {
      let rate = 0.10;
      if (depositDaysLate <= 5) rate = 0.02;
      else if (depositDaysLate <= 15) rate = 0.05;
      currentFtd = unpaidTax * rate;
    }

    const currentStandard = Math.round(currentFtf + currentFtp + currentFtd);
    const currentRelief = isAepEligible ? currentStandard : 0;
    const currentNet = Math.round(currentStandard - currentRelief);

    projection.push({
      month: m,
      standardPenalty: currentStandard,
      aepRelief: currentRelief,
      netOwed: currentNet
    });
  }

  return {
    standardPenalty,
    reliefAmount,
    netPenaltyOwed,
    isEligibleText: isAepEligible ? "Eligible (Waiver Applied)" : "Ineligible (Manual Request Needed)",
    endBalance: reliefAmount, // mapped to savings/relief indicator
    projection
  };
}

/**
 * VA Partial Claim Program Estimator
 * Authorized by VA Home Loan Program Reform Act (signed July 30, 2025).
 * Lets the VA advance funds to cure delinquent VA-backed mortgage arrears
 * as a 0%-interest subordinate lien, repaid at sale/refinance/payoff.
 * Standard cap: 25% of UPB. COVID-era arrears cap: 30% of UPB.
 */
export function calculateVaPartialClaim(
  unpaidPrincipal: number = 300000,
  arrearageAmount: number = 15000,
  hasCovidArrears: string = "no",
  trialPlanCompleted: string = "yes"
) {
  const capRate = hasCovidArrears === "yes" ? 0.30 : 0.25;
  const maxCapAmount = unpaidPrincipal * capRate;
  const estimatedClaimAmount = Math.min(maxCapAmount, arrearageAmount);
  const isCapped = estimatedClaimAmount < arrearageAmount;
  const remainingArrears = arrearageAmount - estimatedClaimAmount;

  return {
    unpaidPrincipal: Math.round(unpaidPrincipal),
    arrearageAmount: Math.round(arrearageAmount),
    maxCapAmount: Math.round(maxCapAmount),
    capRate,
    estimatedClaimAmount: Math.round(estimatedClaimAmount),
    isCappedText: isCapped
      ? "Capped — claim limited by the percentage-of-UPB cap"
      : "Not capped — arrears are fully covered within the cap",
    remainingArrears: Math.round(Math.max(0, remainingArrears)),
    trialPlanRequired: trialPlanCompleted === "yes"
      ? "Completed — 3-month trial payment plan is done"
      : "Required — must complete a 3-month trial payment plan first",
    programSunset: "July 30, 2030",
    servicerDeadline: "November 28, 2026",
    interestRate: "0% — subordinate lien with no monthly payment",
    repaymentTrigger: "Re-paid at property sale, refinance, or loan payoff"
  };
}

/**
 * LRS TCS Calculator (Budget 2026 Changes) for India
 * Calculates Tax Collected at Source on foreign remittances under RBI's Liberalised Remittance Scheme.
 * Budget 2026 (effective Apr 1, 2026) reduced education/medical TCS from 5% to 2% above ₹10L,
 * simplified tour packages to flat 2% (was 5%/20% tiered), general LRS remains 20% above ₹10L.
 */
export function calculateLrsTcs(
  remittanceAmount: number = 500000,
  purpose: string = "education",
  financedByLoan: string = "no",
  cumulativeFYRemittances: number = 0
) {
  const THRESHOLD = 1000000; // ₹10 lakh
  const LRS_LIMIT_USD = 250000;
  const totalRemittances = cumulativeFYRemittances + remittanceAmount;
  const amountAboveThreshold = Math.max(0, totalRemittances - THRESHOLD);
  const amountAboveThresholdThisTx = Math.max(0, totalRemittances - THRESHOLD) - Math.max(0, cumulativeFYRemittances - THRESHOLD);

  let tcsRate = 0;
  let tcsAmount = 0;
  let taxableAmount = 0;
  let effectiveOutflow = remittanceAmount;

  if (purpose === "education_loan") {
    // Education financed by recognized loan institution = 0% TCS, no threshold
    tcsRate = 0;
    taxableAmount = 0;
    tcsAmount = 0;
  } else if (purpose === "education" || purpose === "medical") {
    // Education (self-funded) or medical: 2% above ₹10L cumulative
    tcsRate = 0.02;
    taxableAmount = Math.max(0, amountAboveThresholdThisTx);
    tcsAmount = taxableAmount * tcsRate;
  } else if (purpose === "tour_package") {
    // Overseas tour package: flat 2% from first rupee, no threshold
    tcsRate = 0.02;
    taxableAmount = remittanceAmount;
    tcsAmount = taxableAmount * tcsRate;
  } else {
    // General LRS (gift, investment, family maintenance, etc.): 20% above ₹10L
    tcsRate = 0.20;
    taxableAmount = Math.max(0, amountAboveThresholdThisTx);
    tcsAmount = taxableAmount * tcsRate;
  }

  effectiveOutflow = remittanceAmount + tcsAmount;

  return {
    remittanceAmount: Math.round(remittanceAmount),
    purpose,
    financedByLoan: purpose === "education_loan" ? "yes" : financedByLoan,
    tcsRate: tcsRate * 100,
    taxableAmount: Math.round(taxableAmount),
    tcsAmount: Math.round(tcsAmount),
    effectiveOutflow: Math.round(effectiveOutflow),
    netRemittance: Math.round(remittanceAmount),
    cumulativeFYRemittances: Math.round(cumulativeFYRemittances),
    totalRemittances: Math.round(totalRemittances),
    thresholdExceeded: totalRemittances > THRESHOLD,
    thresholdRemaining: Math.max(0, THRESHOLD - cumulativeFYRemittances - remittanceAmount),
    lrsAnnualLimit: LRS_LIMIT_USD,
    tcsCreditable: "Yes — TCS appears in Form 26AS/AIS and credits against your income tax liability",
    amountAboveThreshold: Math.round(Math.max(0, amountAboveThresholdThisTx)),
    effectiveDate: "April 1, 2026 (already in effect)"
  };
}

export function calculateUkBnplDpcRights(
  purchaseAmount: number = 500,
  repaymentTermMonths: number = 3,
  missedPayments: number = 1,
  provider: string = "klarna",
  purchaseDate: string = "post"
) {
  const isRegulated = purchaseDate === "post";

  // Late fees per missed payment by provider (based on disclosed FCA fee schedules)
  // Klarna: £5/missed, Clearpay: £6/missed, PayPal Pay in 3: £6/missed, Other: £6/missed default
  let lateFeePerMissed = 6;
  if (provider === "klarna") lateFeePerMissed = 5;
  // paypal, clearpay, other → all default to £6
  
  let totalLateFees = lateFeePerMissed * missedPayments;
  const lateFeeCap = purchaseAmount * 0.25;
  if (totalLateFees > lateFeeCap) totalLateFees = lateFeeCap;

  const totalRepayment = Math.round(purchaseAmount + totalLateFees);

  // Regulatory protection checklist
  const fosAccess = isRegulated ? "Authorized (Free Escalation to Ombudsman)" : "None (No independent dispute resolution)";
  const creditCheckType = isRegulated ? "Mandatory (Proportionate assessment — hard or soft, risk-based)" : "Soft search / no debt limit checks";
  const consumerDuty = isRegulated ? "Enforced (Lender must prevent credit spiral)" : "None (Self-regulated voluntary code)";

  // Repayment projection
  const projection: Array<{
    month: number;
    scheduledPayment: number;
    lateFees: number;
    cumulativePaid: number;
    remainingBalance: number;
  }> = [];

  const baseMonthlyPayment = Math.floor(purchaseAmount / repaymentTermMonths);
  let cumulativePaid = 0;
  let remaining = purchaseAmount + totalLateFees;

  projection.push({
    month: 0,
    scheduledPayment: 0,
    lateFees: 0,
    cumulativePaid: 0,
    remainingBalance: Math.round(remaining)
  });

  for (let m = 1; m <= repaymentTermMonths; m++) {
    // Scheduled payment: divide evenly, and allocate the remainder to the final month
    const scheduled = m === repaymentTermMonths 
      ? (purchaseAmount - baseMonthlyPayment * (repaymentTermMonths - 1))
      : baseMonthlyPayment;
      
    const currentLateFee = m === 1 ? totalLateFees : 0;
    
    // For the final month, sweep the exact remaining balance to hit 0
    const paidThisMonth = m === repaymentTermMonths ? remaining : (scheduled + currentLateFee);
    
    cumulativePaid += paidThisMonth;
    remaining -= paidThisMonth;

    projection.push({
      month: m,
      scheduledPayment: Math.round(scheduled),
      lateFees: Math.round(currentLateFee),
      cumulativePaid: Math.round(cumulativePaid),
      remainingBalance: Math.round(Math.max(0, remaining))
    });
  }

  return {
    totalLateFees: Math.round(totalLateFees),
    totalRepayment: Math.round(totalRepayment),
    fosAccess,
    creditCheckType,
    consumerDuty,
    regulationStatus: isRegulated ? "FCA Regulated (Deferred Payment Credit)" : "Unregulated Contract",
    endBalance: Math.round(totalLateFees), // map to total cost additions
    projection
  };
}

export function calculateInstantTaxDeduction(
  grossIncome: number,
  actualExpenses: number = 300,
  hasReceipts: boolean = false
) {
  // Determine Australia marginal rate for FY 2026-27 (Stage 3 Tax Slabs)
  // Slabs:
  // 0 - 18,200: 0%
  // 18,201 - 45,000: 15%
  // 45,001 - 135,000: 30%
  // 135,001 - 190,000: 37%
  // 190,001+: 45%
  let marginalRate = 0;
  if (grossIncome > 190000) {
    marginalRate = 0.45;
  } else if (grossIncome > 135000) {
    marginalRate = 0.37;
  } else if (grossIncome > 45000) {
    marginalRate = 0.30;
  } else if (grossIncome > 18200) {
    marginalRate = 0.15;
  }

  const medicareLevy = 0.02; // Medicare Levy is 2%
  const effectiveRate = marginalRate + medicareLevy;

  // Old rules:
  // - If hasReceipts: can claim actualExpenses.
  // - If no receipts: capped at $300.
  const oldClaim = hasReceipts ? actualExpenses : Math.min(300, actualExpenses);

  // New rules:
  // - Standard deduction is $1,000 (no receipts needed).
  // - If actualExpenses > 1,000 and hasReceipts: can claim actualExpenses (itemized).
  const standardDeduction = 1000;
  const itemizedDeduction = hasReceipts ? actualExpenses : 0;
  const newClaim = (hasReceipts && actualExpenses > 1000) ? actualExpenses : standardDeduction;

  const deductionIncrease = Math.max(0, newClaim - oldClaim);
  const oldTaxSavings = oldClaim * effectiveRate;
  const newTaxSavings = newClaim * effectiveRate;
  const netTaxSaved = deductionIncrease * effectiveRate;

  let homeOfficeNote = "Standard $1,000 deduction covers general work expenses without receipts.";
  if (hasReceipts && actualExpenses > 1000) {
    homeOfficeNote = "Itemized actual expenses exceed $1,000 standard limit; claim itemized expenses with logbook & receipts.";
  } else if (hasReceipts) {
    homeOfficeNote = "Standard $1,000 deduction is higher than your itemized expenses ($" + actualExpenses + "); claim the $1,000 standard deduction.";
  }

  return {
    marginalRate: marginalRate * 100,
    medicareLevy: medicareLevy * 100,
    effectiveRate: effectiveRate * 100,
    standardDeduction,
    itemizedDeduction: Math.round(itemizedDeduction),
    oldClaim: Math.round(oldClaim),
    newClaim: Math.round(newClaim),
    recommendedClaim: Math.round(newClaim),
    deductionIncrease: Math.round(deductionIncrease),
    oldTaxSavings: Math.round(oldTaxSavings),
    newTaxSavings: Math.round(newTaxSavings),
    netTaxSaved: Math.round(netTaxSaved),
    homeOfficeOverlap: homeOfficeNote,
    endBalance: Math.round(netTaxSaved), // mapping for layout card
  };
}

/**
 * CIBIL Update Date Predictor
 * RBI Direction RBI/DOR/2025-26/119 (effective July 1, 2026)
 * Banks must report credit data to CICs on the 9th, 16th, 23rd, and month-end.
 * Bank has 4 calendar days from reference date to submit data.
 * CIC has 5 calendar days to ingest and reflect updates.
 */
export function calculateCibilUpdatePredictor(
  paymentDate: number = 1,
  paymentMonth: number = 7
) {
  // Let's determine the next reference date after the payment date
  // The monthly reference dates are: 9, 16, 23, and month-end
  let refDay = 9;
  let cycleName = "Weekly Cycle 1 (9th)";
  
  if (paymentDate <= 9) {
    refDay = 9;
    cycleName = "Weekly Cycle 1 (9th)";
  } else if (paymentDate <= 16) {
    refDay = 16;
    cycleName = "Weekly Cycle 2 (16th)";
  } else if (paymentDate <= 23) {
    refDay = 23;
    cycleName = "Weekly Cycle 3 (23rd)";
  } else {
    refDay = 30; // We'll model month-end as 30th/31st depending on month, but reference day is Month-End
    cycleName = "Weekly Cycle 4 (Month-End)";
  }

  // Get month names
  const MONTHS = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const monthName = MONTHS[paymentMonth];
  const nextMonthNum = paymentMonth === 12 ? 1 : paymentMonth + 1;
  const nextMonthName = MONTHS[nextMonthNum];

  // Formatting strings for output
  let refDateStr = "";
  let submissionDeadlineStr = "";
  let reflectionStartStr = "";
  let reflectionEndStr = "";
  let delayDays = 0;

  if (refDay === 9) {
    refDateStr = `9th ${monthName} 2026`;
    submissionDeadlineStr = `13th ${monthName} 2026`;
    reflectionStartStr = `10th ${monthName} 2026`;
    reflectionEndStr = `18th ${monthName} 2026`;
    delayDays = 9 - paymentDate + 4 + 5; // days from payment to ref date + 4 (bank) + 5 (CIC)
  } else if (refDay === 16) {
    refDateStr = `16th ${monthName} 2026`;
    submissionDeadlineStr = `20th ${monthName} 2026`;
    reflectionStartStr = `17th ${monthName} 2026`;
    reflectionEndStr = `25th ${monthName} 2026`;
    delayDays = 16 - paymentDate + 4 + 5;
  } else if (refDay === 23) {
    refDateStr = `23rd ${monthName} 2026`;
    submissionDeadlineStr = `27th ${monthName} 2026`;
    reflectionStartStr = `24th ${monthName} 2026`;
    reflectionEndStr = `2nd ${nextMonthName} 2026`;
    delayDays = 23 - paymentDate + 4 + 5;
  } else {
    // Month-End. Let's find days in month
    let daysInMonth = 30;
    if ([1, 3, 5, 7, 8, 10, 12].includes(paymentMonth)) daysInMonth = 31;
    else if (paymentMonth === 2) daysInMonth = 28; // 2026 is not a leap year

    refDateStr = `${daysInMonth === 31 ? "31st" : "30th"} ${monthName} 2026`;
    submissionDeadlineStr = `5th ${nextMonthName} 2026`;
    reflectionStartStr = `1st ${nextMonthName} 2026`;
    reflectionEndStr = `10th ${nextMonthName} 2026`;
    delayDays = daysInMonth - paymentDate + 4 + 5;
  }

  return {
    nextReferenceDate: refDateStr,
    reportingDeadline: submissionDeadlineStr,
    reflectionStart: reflectionStartStr,
    reflectionEnd: reflectionEndStr,
    delayDays: delayDays,
    regulationText: "RBI Directions RBI/DOR/2025-26/119 (effective July 1, 2026) mandating weekly data uploads by banks within 4 days and ingestion by CICs within 5 days."
  };
}

/**
 * German KVdR 9/10 Rule Eligibility Calculator
 * Evaluates whether a pensioner qualifies for compulsory health insurance status (KVdR).
 * Requires GKV coverage for >= 90% of the second half of their working life.
 * Grants a credit of 3 years (36 months) per child.
 */
export function calculateKvdrEligibility(
  workingStartAge: number = 20,
  retirementAge: number = 67,
  gkvYearsSecondHalf: number = 15,
  childrenCount: number = 0
) {
  if (![workingStartAge, retirementAge, gkvYearsSecondHalf, childrenCount].every(Number.isFinite) ||
      retirementAge <= workingStartAge || gkvYearsSecondHalf < 0 || childrenCount < 0) {
    throw new RangeError("Invalid KVdR insurance-history inputs");
  }
  const totalWorkingLife = retirementAge - workingStartAge;
  const secondHalfDuration = totalWorkingLife / 2;
  const secondHalfStart = workingStartAge + secondHalfDuration;
  
  // 9/10 rule requirement duration
  const requiredGkvYears = 0.9 * secondHalfDuration;
  
  // Child credit = 3 years per child
  const childCredits = childrenCount * 3;
  
  // Virtual GKV years is actual years in the second half + child credits
  // Note: Virtual GKV years cannot exceed the actual duration of the second half of working life.
  const actualGkvYears = Math.min(secondHalfDuration, gkvYearsSecondHalf);
  const impossibleHistory = gkvYearsSecondHalf > secondHalfDuration;
  const totalGkvYears = Math.min(secondHalfDuration, actualGkvYears + childCredits);
  
  const isEligible = totalGkvYears >= requiredGkvYears;
  const yearsShort = Math.max(0, requiredGkvYears - totalGkvYears);

  return {
    totalWorkingLife: Number(totalWorkingLife.toFixed(2)),
    secondHalfStart: Number(secondHalfStart.toFixed(2)),
    requiredGkvYears: Number(requiredGkvYears.toFixed(2)),
    actualGkvYears: Number(actualGkvYears.toFixed(2)),
    childCredits: Number(childCredits.toFixed(2)),
    totalGkvYears: Number(totalGkvYears.toFixed(2)),
    isEligibleText: impossibleHistory
      ? `CHECK INPUT: the second half is only ${secondHalfDuration.toFixed(2)} years; entered GKV years cannot exceed it.`
      : isEligible
        ? "9/10 TEST MET on these approximate ages and GKV years. A health fund must confirm KVdR status; occupational pensions and some work income may also be assessable."
        : "9/10 TEST NOT MET on these approximate inputs. Ask the health fund to verify your exact dates, child credits and lawful voluntary GKV or private-cover options.",
    yearsShort: Number(yearsShort.toFixed(2)),
    endBalance: isEligible && !impossibleHistory ? 1 : 0
  };
}

/**
 * Australian Working Holiday Maker (WHM) Net Pay & Take-Home Calculator (2025-26)
 * Calculates tax withheld for subclass 417/462 visa holders.
 * Supports registered (15% rate) vs unregistered (30% foreign resident rate) employers.
 * Calculates superannuation DASP refund (65% tax rate applied to net super balance).
 * Contrasts net pay against standard Australian tax resident rates (Stage 3 cuts).
 */
export function calculateWhmTaxTakeHome(
  wageType: string = "hourly",
  wageAmount: number = 30,
  hoursPerWeek: number = 38,
  employerRegistered: string = "registered"
) {
  // 1. Calculate Gross Pay
  let weeklyGrossPay = 0;
  if (wageType === "hourly") {
    weeklyGrossPay = wageAmount * hoursPerWeek;
  } else {
    weeklyGrossPay = wageAmount;
  }

  const annualGrossPay = weeklyGrossPay * 52;

  // 2. Calculate WHM Tax Withheld (2025-26 ATO rates)
  // Registered WHM rates:
  // - 0 to 45,000: 15%
  // - 45,001 to 135,000: 30%
  // - 135,001 to 190,000: 37%
  // - 190,001+: 45%
  // Unregistered WHM rates (foreign resident rates):
  // - 0 to 135,000: 30%
  // - 135,001 to 190,000: 37%
  // - 190,001+: 45%
  let annualTaxWithheld = 0;
  const isRegistered = employerRegistered === "registered";

  if (isRegistered) {
    if (annualGrossPay <= 45000) {
      annualTaxWithheld = annualGrossPay * 0.15;
    } else if (annualGrossPay <= 135000) {
      annualTaxWithheld = 45000 * 0.15 + (annualGrossPay - 45000) * 0.30;
    } else if (annualGrossPay <= 190000) {
      annualTaxWithheld = 45000 * 0.15 + 90000 * 0.30 + (annualGrossPay - 135000) * 0.37;
    } else {
      annualTaxWithheld = 45000 * 0.15 + 90000 * 0.30 + 55000 * 0.37 + (annualGrossPay - 190000) * 0.45;
    }
  } else {
    // Unregistered (Foreign Resident Rates)
    if (annualGrossPay <= 135000) {
      annualTaxWithheld = annualGrossPay * 0.30;
    } else if (annualGrossPay <= 190000) {
      annualTaxWithheld = 135000 * 0.30 + (annualGrossPay - 135000) * 0.37;
    } else {
      annualTaxWithheld = 135000 * 0.30 + 55000 * 0.37 + (annualGrossPay - 190000) * 0.45;
    }
  }

  const annualNetPay = annualGrossPay - annualTaxWithheld;
  const weeklyTaxWithheld = annualTaxWithheld / 52;
  const weeklyNetPay = weeklyGrossPay - weeklyTaxWithheld;

  // 3. Superannuation and DASP Refund
  // Super Guarantee is 12.0% for 2025-26
  const annualSuperContribution = annualGrossPay * 0.12;
  // Super contributions are taxed at 15% upon entry into the fund
  const superNetInFund = annualSuperContribution * 0.85;
  // DASP tax for WHMs is 65%
  const daspTax = superNetInFund * 0.65;
  const daspSuperRefund = superNetInFund - daspTax;

  // 4. Comparison with standard Australian resident rates (Stage 3 cuts, 2026-27)
  // Resident rates:
  // - 0 to 18,200: Nil
  // - 18,201 to 45,000: 15% (updated from 16% for 2026-27)
  // - 45,001 to 135,000: 30%
  // - 135,001 to 190,000: 37%
  // - 190,001+: 45%
  // Plus Medicare levy of 2% (WHMs are exempt from Medicare levy)
  let residentIncomeTax = 0;
  if (annualGrossPay <= 18200) {
    residentIncomeTax = 0;
  } else if (annualGrossPay <= 45000) {
    residentIncomeTax = (annualGrossPay - 18200) * 0.15;
  } else if (annualGrossPay <= 135000) {
    residentIncomeTax = (45000 - 18200) * 0.15 + (annualGrossPay - 45000) * 0.30;
  } else if (annualGrossPay <= 190000) {
    residentIncomeTax = (45000 - 18200) * 0.15 + 90000 * 0.30 + (annualGrossPay - 135000) * 0.37;
  } else {
    residentIncomeTax = (45000 - 18200) * 0.15 + 90000 * 0.30 + 55000 * 0.37 + (annualGrossPay - 190000) * 0.45;
  }

  const medicareLevy = annualGrossPay * 0.02;
  const residentTaxComparison = residentIncomeTax + medicareLevy;
  const residentNetComparison = annualGrossPay - residentTaxComparison;

  const takeHomeDifference = annualNetPay - residentNetComparison;

  return {
    weeklyGrossPay: Number(weeklyGrossPay.toFixed(2)),
    weeklyNetPay: Number(weeklyNetPay.toFixed(2)),
    weeklyTaxWithheld: Number(weeklyTaxWithheld.toFixed(2)),
    annualGrossPay: Number(annualGrossPay.toFixed(2)),
    annualNetPay: Number(annualNetPay.toFixed(2)),
    annualTaxWithheld: Number(annualTaxWithheld.toFixed(2)),
    annualSuperContribution: Number(annualSuperContribution.toFixed(2)),
    daspTax: Number(daspTax.toFixed(2)),
    daspSuperRefund: Number(daspSuperRefund.toFixed(2)),
    residentTaxComparison: Number(residentTaxComparison.toFixed(2)),
    residentNetComparison: Number(residentNetComparison.toFixed(2)),
    takeHomeDifference: Number(takeHomeDifference.toFixed(2)),
    takeHome: Number(annualNetPay.toFixed(2)),
    totalTax: Number(annualTaxWithheld.toFixed(2)),
    endBalance: annualNetPay
  };
}

/**
 * NZ Overseas Student Loan Repayment & Amortization Calculator (2026 rules)
 * Simulates NZ student loan payoff schedule for overseas-based borrowers.
 * Interest rate: 5.6% compounding annually (calculated daily, applied annually).
 * Compulsory repayment brackets are based on the initial balance of each year.
 * Compulsory repayment brackets:
 * - < $1,000: Full balance
 * - $1,000 to $15,000: $1,000/year
 * - $15,001 to $30,000: $2,000/year
 * - $30,001 to $45,000: $3,000/year
 * - $45,001 to $60,000: $4,000/year
 * - Over $60,000: $5,000/year
 */
export function calculateOverseasStudentLoanRepayment(
  loanBalance: number = 25000,
  annualExtraRepayments: number = 1000
) {
  const interestRate = 0.056; // 5.6% for overseas-based borrowers in 2026
  
  // Calculate first-year figures
  let compulsoryAnnualRepayment = 0;
  if (loanBalance < 1000) {
    compulsoryAnnualRepayment = loanBalance;
  } else if (loanBalance <= 15000) {
    compulsoryAnnualRepayment = 1000;
  } else if (loanBalance <= 30000) {
    compulsoryAnnualRepayment = 2000;
  } else if (loanBalance <= 45000) {
    compulsoryAnnualRepayment = 3000;
  } else if (loanBalance <= 60000) {
    compulsoryAnnualRepayment = 4000;
  } else {
    compulsoryAnnualRepayment = 5000;
  }

  const totalRepaymentYearly = compulsoryAnnualRepayment + annualExtraRepayments;
  const interestChargedFirstYear = loanBalance * interestRate;

  // Run simulation
  let balance = loanBalance;
  let totalInterestAccrued = 0;
  let totalRepaymentsPaid = 0;
  let payoffDuration = 0;
  const maxYears = 100;
  let isIndefiniteGrow = false;
  const projection: Array<{ year: number; balance: number; repayment: number; interest: number }> = [];

  while (balance > 0.01 && payoffDuration < maxYears) {
    payoffDuration++;
    const startBalance = balance;
    
    let reqRepayment = 0;
    if (startBalance < 1000) {
      reqRepayment = startBalance;
    } else if (startBalance <= 15000) {
      reqRepayment = 1000;
    } else if (startBalance <= 30000) {
      reqRepayment = 2000;
    } else if (startBalance <= 45000) {
      reqRepayment = 3000;
    } else if (startBalance <= 60000) {
      reqRepayment = 4000;
    } else {
      reqRepayment = 5000;
    }

    const yearlyInterest = startBalance * interestRate;
    totalInterestAccrued += yearlyInterest;

    const yearlyPayment = Math.min(startBalance + yearlyInterest, reqRepayment + annualExtraRepayments);
    totalRepaymentsPaid += yearlyPayment;

    if (yearlyInterest >= (reqRepayment + annualExtraRepayments) && startBalance > 100) {
      isIndefiniteGrow = true;
      break;
    }

    balance = startBalance + yearlyInterest - yearlyPayment;

    projection.push({
      year: payoffDuration,
      balance: Number(balance.toFixed(2)),
      repayment: Number(yearlyPayment.toFixed(2)),
      interest: Number(yearlyInterest.toFixed(2))
    });
  }

  let payoffStatus = "";
  if (isIndefiniteGrow) {
    payoffStatus = "WARNING: Your annual payments ($" + totalRepaymentYearly.toLocaleString() + ") are less than the compounding interest ($" + interestChargedFirstYear.toLocaleString() + "). Your loan balance will grow indefinitely. Increase your voluntary extra payments.";
    payoffDuration = 999; // Represents infinite
  } else {
    payoffStatus = "HEALTHY: Your loan will be fully paid off in approximately " + payoffDuration + " years with a total of $" + totalInterestAccrued.toLocaleString(undefined, { maximumFractionDigits: 0 }) + " paid in interest.";
  }

  return {
    currentInterestRate: "5.60% p.a. (Late payment: 9.60%)",
    compulsoryAnnualRepayment: Number(compulsoryAnnualRepayment.toFixed(2)),
    totalRepaymentYearly: Number(totalRepaymentYearly.toFixed(2)),
    interestChargedFirstYear: Number(interestChargedFirstYear.toFixed(2)),
    payoffDuration: isIndefiniteGrow ? 999 : payoffDuration,
    totalRepaymentsPaid: isIndefiniteGrow ? 999999 : Number(totalRepaymentsPaid.toFixed(2)),
    totalInterestAccrued: isIndefiniteGrow ? 999999 : Number(totalInterestAccrued.toFixed(2)),
    payoffStatus,
    projection,
    useYearLabel: true,
    endBalance: isIndefiniteGrow ? 999999 : Number(balance.toFixed(2))
  };
}

/**
 * Netherlands ZZP (Freelance) Tax Deductions & Take-Home Calculator
 * 
 * 2026 Rules:
 * - Zelfstandigenaftrek (Self-employed deduction): €1,200
 * - Startersaftrek (Starters deduction): €2,123
 * - MKB-winstvrijstelling (SME profit exemption): 12.7%
 * - Box 1 Brackets (2026):
 *   - Up to €38,883: 35.75% (includes 27.65% Volksverzekeringen)
 *   - €38,883 to €78,426: 37.56%
 *   - Above €78,426: 49.50%
 * - ZVW (healthcare contribution) self-employed: 4.85% capped at €79,409
 */
export function calculateZzpTaxDeductionTakeHome(
  grossRevenues: number = 60000,
  businessExpenses: number = 10000,
  meetUrencriterium: boolean = true,
  isStarter: boolean = false
) {
  const grossProfit = Math.max(0, grossRevenues - businessExpenses);

  // Entrepreneurial Deductions
  const zelfstandigenaftrek = meetUrencriterium ? 1200 : 0;
  const startersaftrek = (meetUrencriterium && isStarter) ? 2123 : 0;

  const profitAfterAftrek = Math.max(0, grossProfit - zelfstandigenaftrek - startersaftrek);
  const mkbWinstvrijstelling = Number((profitAfterAftrek * 0.127).toFixed(2));
  
  const taxableProfit = Math.max(0, profitAfterAftrek - mkbWinstvrijstelling);
  const totalDeductions = zelfstandigenaftrek + startersaftrek + mkbWinstvrijstelling;

  // Box 1 Income Tax & Social Premiums (Volksverzekeringen)
  // Progressive Brackets
  let baseTax = 0;
  if (taxableProfit <= 38883) {
    baseTax = taxableProfit * 0.3575;
  } else if (taxableProfit <= 78426) {
    baseTax = (38883 * 0.3575) + ((taxableProfit - 38883) * 0.3756);
  } else {
    baseTax = (38883 * 0.3575) + ((78426 - 38883) * 0.3756) + ((taxableProfit - 78426) * 0.4950);
  }

  // General Tax Credit (Algemene heffingskorting)
  let genCredit = 0;
  if (taxableProfit <= 29736) {
    genCredit = 3115;
  } else {
    genCredit = Math.max(0, 3115 - (taxableProfit - 29736) * 0.06398);
  }

  // Labor Tax Credit (Arbeidskorting)
  let workCredit = 0;
  if (grossProfit <= 45592) {
    workCredit = grossProfit === 0 ? 0 : Math.min(5685, 5685 * (grossProfit / 30000));
  } else {
    workCredit = Math.max(0, 5685 - (grossProfit - 45592) * 0.0651);
  }

  const combinedTaxAndPremium = Math.max(0, baseTax - genCredit - workCredit);
  const rawSocialSecurity = Math.min(taxableProfit, 38883) * 0.2765;
  const socialSecurity = Math.min(rawSocialSecurity, combinedTaxAndPremium);
  const box1Tax = Number(combinedTaxAndPremium.toFixed(2));

  // Healthcare Contribution (ZVW, 4.85% capped at €79,409)
  const zvwContribution = Number((Math.min(taxableProfit, 79409) * 0.0485).toFixed(2));

  const totalTaxesPaid = box1Tax + zvwContribution;
  const netIncomeAnnual = Math.max(0, grossProfit - totalTaxesPaid);
  const netIncomeMonthly = netIncomeAnnual / 12;

  return {
    grossProfit: Number(grossProfit.toFixed(2)),
    zelfstandigenaftrek: Number(zelfstandigenaftrek.toFixed(2)),
    startersaftrek: Number(startersaftrek.toFixed(2)),
    mkbWinstvrijstelling: Number(mkbWinstvrijstelling.toFixed(2)),
    taxableProfit: Number(taxableProfit.toFixed(2)),
    box1Tax: Number(box1Tax.toFixed(2)),
    zvwContribution: Number(zvwContribution.toFixed(2)),
    totalDeductions: Number(totalDeductions.toFixed(2)),
    netIncomeAnnual: Number(netIncomeAnnual.toFixed(2)),
    netIncomeMonthly: Number(netIncomeMonthly.toFixed(2)),
    takeHome: Number(netIncomeAnnual.toFixed(2)),
    totalTax: Number(totalTaxesPaid.toFixed(2))
  };
}

/**
 * India Labour Codes wage add-back and EPFO payroll scenario, September 2026.
 * MoLE's 16 March FAQ says section 2(y) adds excluded remuneration above 50%
 * back to defined wages. It does NOT force basic pay to equal half of CTC.
 * Employer PF/pension and statutory bonus belong in the remuneration test;
 * gratuity and ESI are outside this test. Inputs are the user's own components.
 * The September 17 notification raised the EPFO contribution ceiling to
 * Rs 25,000 a month, unless higher-wage contributions are agreed.
 * https://www.labour.gov.in/static/uploads/2026/03/a4ccf4c6d97c4f1f36a6d83f8c64213d.pdf
 * https://www.pib.gov.in/PressReleasePage.aspx?PRID=2314111&lang=2&reg=48
 * https://www.epfindia.gov.in/site_docs/PDFs/MiscPDFs/ContributionRate.pdf
 */
export function calculateNewLabourCodesCtcRestructuring(
  includedWagesMonthly: number = 40000,
  excludedCashMonthly: number = 50000,
  employerPfMonthly: number = 3000,
  statutoryBonusMonthly: number = 0,
  pfCoverage: "yes" | "no" = "yes",
  pfRate: 12 | 10 = 12,
  pfContributionMode: "statutory_cap" | "higher_agreed" = "statutory_cap"
) {
  const money = (value: number) => Math.round(value * 100) / 100;
  const safe = (value: number) => Number.isFinite(value) ? Math.max(0, Math.min(5_000_000, value)) : 0;
  const included = safe(includedWagesMonthly);
  const cashExclusions = safe(excludedCashMonthly);
  const reportedEmployerPf = safe(employerPfMonthly);
  const statutoryBonus = safe(statutoryBonusMonthly);
  const remunerationForTest = money(included + cashExclusions + reportedEmployerPf + statutoryBonus);
  const excludedForTest = money(cashExclusions + reportedEmployerPf + statutoryBonus);
  const addBack = money(Math.max(0, excludedForTest - remunerationForTest / 2));
  const codeWage = money(included + addBack);
  const rate = pfRate === 10 ? 10 : 12;
  const isMember = pfCoverage === "yes";
  const pfWage = !isMember ? 0 : pfContributionMode === "higher_agreed" ? codeWage : Math.min(codeWage, 25_000);
  const employeePf = money(pfWage * rate / 100);
  const employerPfScenario = money(pfWage * rate / 100);
  const grossCashMonthly = money(included + cashExclusions + statutoryBonus);
  const cashAfterEmployeePf = money(Math.max(0, grossCashMonthly - employeePf));
  // 15 days' last-drawn wages per completed year, expressed as a reference
  // amount for one year. Actual gratuity is subject to tenure and other law.
  const oneYearGratuityReference = money(codeWage * 15 / 26);

  return {
    remunerationForTest,
    excludedForTest,
    allowanceAddBack: addBack,
    codeWageBase: codeWage,
    grossCashMonthly,
    pfContributionWage: money(pfWage),
    employeePfMonthly: employeePf,
    employerEpfoMonthly: employerPfScenario,
    cashAfterEmployeePfBeforeTax: cashAfterEmployeePf,
    oneYearGratuityReference,
    wageSharePercent: remunerationForTest > 0 ? money(codeWage / remunerationForTest * 100) : 0,
    wageRuleNotice: "The 50% rule adds excluded remuneration above half the test total back to defined wages. It does not order an employer to set Basic + DA at 50% of CTC or cut salary.",
    pfNotice: !isMember
      ? "No EPF deduction is modeled because you selected not covered. The tool cannot determine whether you must join; check the employer and EPFO."
      : pfContributionMode === "higher_agreed"
        ? `Higher-wage PF is modeled on the full computed wage at ${rate}%. Confirm the written arrangement and update the employer PF input to the amount in that package if it differs; this is not the automatic statutory minimum.`
        : `The ${rate}% PF scenario uses the lower of the computed monthly wage and the ₹25,000 contribution ceiling effective 17 September 2026. Actual enrollment and any 10% exception need confirmation.`,
    gratuityNotice: "The one-year gratuity figure uses 15/26 of the computed monthly wage as a reference. It is not cash paid each month or a promise of eligibility; tenure and the applicable scheme decide an actual award."
  };
}

/**
 * IRS Mid-Year Standard Mileage Rate Split Calculator (2026)
 * Official Sourcing: IRS Announcement 2026-11 (modifying Notice 2026-10).
 * Business H1 (Jan 1 - Jun 30): 72.5¢/mile ($0.725)
 * Business H2 (Jul 1 - Dec 31): 76.0¢/mile ($0.760)
 * Medical/Moving H1: 20.5¢/mile ($0.205)
 * Medical/Moving H2: 23.5¢/mile ($0.235)
 * Charitable: 14.0¢/mile ($0.14 - statutory)
 */
export function calculateIrsMileageRateSplit(
  businessMilesH1: number = 5000,
  businessMilesH2: number = 5000,
  medicalMilesH1: number = 0,
  medicalMilesH2: number = 0,
  charityMiles: number = 0,
  marginalTaxRate: number = 24,
  usedActualExpensesH1: "no" | "yes" = "no",
  medicalRateH2: number = 0.235
) {
  const rateBusinessH1 = 0.725;
  const rateBusinessH2 = 0.760;
  const rateMedicalH1 = 0.205;
  const rateMedicalH2 = Number(medicalRateH2) || 0.235;
  const rateCharity = 0.14;

  const isDisallowed = usedActualExpensesH1 === "yes";

  const h1BusinessDeduction = (businessMilesH1 || 0) * rateBusinessH1;
  const h2BusinessDeduction = (businessMilesH2 || 0) * rateBusinessH2;
  const totalBusinessDeduction = h1BusinessDeduction + h2BusinessDeduction;

  const h1MedicalDeduction = (medicalMilesH1 || 0) * rateMedicalH1;
  const h2MedicalDeduction = (medicalMilesH2 || 0) * rateMedicalH2;
  const medicalMovingDeduction = h1MedicalDeduction + h2MedicalDeduction;

  const charityDeduction = (charityMiles || 0) * rateCharity;
  const rawTotalMileageDeduction = totalBusinessDeduction + medicalMovingDeduction + charityDeduction;
  const totalMileageDeduction = isDisallowed ? 0 : rawTotalMileageDeduction;

  const marginalRateDecimal = (marginalTaxRate || 0) / 100;
  const estimatedTaxSavings = totalMileageDeduction * marginalRateDecimal;

  const totalBusinessMiles = (businessMilesH1 || 0) + (businessMilesH2 || 0);
  const blendedBusinessRate = totalBusinessMiles > 0
    ? (totalBusinessDeduction / totalBusinessMiles) * 100
    : 76.0;

  const disallowedWarning = isDisallowed
    ? "DISALLOWED: Under IRS Rev. Proc. 2019-46, vehicles using the 'Actual Expenses' method in H1 cannot switch to the Standard Mileage Rate in H2."
    : "IRS Compliant: Standard Mileage Rate split applied (H1 72.5¢ / H2 76.0¢ business; H1 20.5¢ / H2 23.5¢ medical).";

  return {
    h1BusinessDeduction: Number(h1BusinessDeduction.toFixed(2)),
    h2BusinessDeduction: Number(h2BusinessDeduction.toFixed(2)),
    totalBusinessDeduction: Number(totalBusinessDeduction.toFixed(2)),
    medicalMovingDeduction: Number(medicalMovingDeduction.toFixed(2)),
    charityDeduction: Number(charityDeduction.toFixed(2)),
    totalMileageDeduction: Number(totalMileageDeduction.toFixed(2)),
    estimatedTaxSavings: Number(estimatedTaxSavings.toFixed(2)),
    blendedBusinessRate: Number(blendedBusinessRate.toFixed(2)),
    isDisallowed,
    disallowedWarning,
    notice: "IRS Announcement 2026-11 (H2 2026 Mid-Year Standard Mileage Rate Adjustment)"
  };
}

/**
 * India HRA Tax Exemption Calculator (8-City Expanded Draft, FY 2026-27)
 * Official Sourcing: Draft Income-tax Rules, 2026 (proposed ~Feb 2026).
 * 8 Metro Cities (50% exemption): Mumbai, Delhi, Kolkata, Chennai, Bengaluru, Pune, Hyderabad, Ahmedabad.
 * All other cities (40% exemption).
 * Old Tax Regime only (Exemption is ₹0 in New Tax Regime).
 * Mandatory Landlord PAN disclosure if rent > ₹1,00,000/year.
 */
export function calculateHraExemption8Cities(
  basicSalaryAnnual: number = 600000,
  daAnnual: number = 0,
  hraReceivedAnnual: number = 240000,
  rentPaidAnnual: number = 300000,
  cityCategory: "metro8" | "nonmetro" = "metro8",
  taxRegime: "old" | "new" = "old",
  marginalTaxRate?: number
) {
  const salaryBase = (basicSalaryAnnual || 0) + (daAnnual || 0);

  // Old Tax Regime slab tax calculator with Section 87A rebate & 4% cess
  const computeOldRegimeTax = (income: number): number => {
    if (income <= 0) return 0;
    let baseTax = 0;
    if (income > 1000000) {
      baseTax = 12500 + 100000 + (income - 1000000) * 0.30;
    } else if (income > 500000) {
      baseTax = 12500 + (income - 500000) * 0.20;
    } else if (income > 250000) {
      baseTax = (income - 250000) * 0.05;
    }
    // Section 87A Rebate: 100% tax rebate if taxable income <= ₹5,00,000
    if (income <= 500000) {
      baseTax = 0;
    }
    return Math.round(baseTax * 1.04);
  };

  if (taxRegime === "new") {
    return {
      salaryBase: Math.round(salaryBase),
      actualHraAnnual: Math.round(hraReceivedAnnual),
      rentMinus10PercentSalary: Math.max(0, Math.round((rentPaidAnnual || 0) - 0.10 * salaryBase)),
      cityLimitAmount: Math.round((cityCategory === "metro8" ? 0.50 : 0.40) * salaryBase),
      exemptHra: 0,
      exemptHraAnnual: 0,
      taxableHra: Math.round(hraReceivedAnnual),
      taxableHraAnnual: Math.round(hraReceivedAnnual),
      taxSaved: 0,
      annualTaxSavings: 0,
      panRequiredNote: rentPaidAnnual > 100000 ? "Form 124 Mandate: Landlord PAN & Relationship Declaration mandatory (Rent > ₹1,00,000/yr)" : "No PAN requirement",
      regimeNote: "HRA exemption is NOT allowed under the New Tax Regime (Section 115BAC). Switch to Old Tax Regime to claim.",
      ruleStatus: "CBDT Notified: Income-tax Rules, 2026 (Notification No. G.S.R. 198(E), March 20, 2026)."
    };
  }

  const limit1 = Math.max(0, hraReceivedAnnual || 0);
  const limit2 = cityCategory === "metro8" ? 0.50 * salaryBase : 0.40 * salaryBase;
  const limit3 = Math.max(0, (rentPaidAnnual || 0) - 0.10 * salaryBase);

  const exemptHra = Math.min(limit1, limit2, limit3);
  const taxableHra = Math.max(0, limit1 - exemptHra);

  // Compute exact tax savings as difference between tax without HRA exemption and tax with HRA exemption
  const taxBeforeHra = computeOldRegimeTax(salaryBase);
  const taxableSalaryAfterHra = Math.max(0, salaryBase - exemptHra);
  const taxAfterHra = computeOldRegimeTax(taxableSalaryAfterHra);
  
  const taxSaved = Math.max(0, taxBeforeHra - taxAfterHra);

  const panRequiredNote = (rentPaidAnnual || 0) > 100000
    ? "Form 124 Mandate: Landlord PAN & Relationship Declaration mandatory (Rent > ₹1,00,000/yr)"
    : "PAN not required (Rent ≤ ₹1,00,000/yr)";

  const cityNote = cityCategory === "metro8"
    ? "50% Basic+DA Exemption Ceiling (8 Notified Metros: Mumbai, Delhi, Kolkata, Chennai, Bengaluru, Pune, Hyderabad, Ahmedabad)"
    : "40% Basic+DA Exemption Ceiling (Non-Metro / Other Cities)";

  return {
    salaryBase: Math.round(salaryBase),
    actualHraAnnual: Math.round(limit1),
    rentMinus10PercentSalary: Math.round(limit3),
    cityLimitAmount: Math.round(limit2),
    exemptHra: Math.round(exemptHra),
    exemptHraAnnual: Math.round(exemptHra),
    taxableHra: Math.round(taxableHra),
    taxableHraAnnual: Math.round(taxableHra),
    taxSaved: Math.round(taxSaved),
    annualTaxSavings: Math.round(taxSaved),
    effectiveTaxRate: marginalTaxRate || (exemptHra > 0 ? Number(((taxSaved / exemptHra) * 100).toFixed(1)) : 0),
    panRequiredNote,
    regimeNote: cityNote,
    ruleStatus: "Finalized CBDT Rule: Officially notified under Income-tax Rules, 2026 (Notification No. G.S.R. 198(E) on March 20, 2026, effective April 1, 2026)."
  };
}

/**
 * Australia Stage 3 Tax Cut 15% Lowest Bracket & Sole Trader Asset Write-Off Simulator (FY 2026-27)
 * Sourced from ATO & Federal Budget 2026-27 (Effective 1 July 2026).
 * Lowest Bracket Rate: 16% -> 15% (for $18,201 to $45,000).
 * Permanent $20,000 Instant Asset Write-off for small businesses (< $10m turnover).
 */
export function calculateStage3TaxCut15PercentBracket(
  taxableSalary: number = 75000,
  soleTraderNetIncome: number = 25000,
  eligibleAssetPurchases: number = 12000,
  businessUsePercent: number = 100
) {
  const totalGrossIncome = (taxableSalary || 0) + (soleTraderNetIncome || 0);
  // ?? not ||: a genuine 0% business-use entry must zero the write-off, not
  // silently restore the 100% default.
  const validBusinessUseRatio = Math.max(0, Math.min(100, businessUsePercent ?? 100)) / 100;

  // Instant Asset Write-Off Math (applied directly against sole trader/business income, capped at $20,000 per asset * business-use %)
  const cappedWriteOffDeduction = Math.min(20000, Math.min(soleTraderNetIncome || 0, Math.max(0, eligibleAssetPurchases || 0))) * validBusinessUseRatio;
  const netTaxableIncome = Math.max(0, totalGrossIncome - cappedWriteOffDeduction);

  // Helper: AU Tax Brackets (excluding 2% Medicare Levy)
  const computeAuTax = (income: number, lowestBracketRate: number) => {
    let tax = 0;
    if (income > 190000) {
      tax += (income - 190000) * 0.45;
      income = 190000;
    }
    if (income > 135000) {
      tax += (income - 135000) * 0.37;
      income = 135000;
    }
    if (income > 45000) {
      tax += (income - 45000) * 0.30;
      income = 45000;
    }
    if (income > 18200) {
      tax += (income - 18200) * lowestBracketRate;
    }
    return tax;
  };

  // 1. Old FY 2025-26 Tax (16% lowest bracket, without write-off)
  const oldTotalTax = computeAuTax(totalGrossIncome, 0.16);

  // 2. New FY 2026-27 Tax (15% lowest bracket, with $20k instant asset write-off)
  const newTotalTax = computeAuTax(netTaxableIncome, 0.15);

  // Direct Tax Cut Savings from 16% -> 15% shift
  const taxCutSavings15Percent = Math.round(computeAuTax(totalGrossIncome, 0.16) - computeAuTax(totalGrossIncome, 0.15));
  const writeOffTaxSavings = Math.round(computeAuTax(totalGrossIncome, 0.15) - newTotalTax);
  const totalCombinedSavings = Math.round(oldTotalTax - newTotalTax);

  // 2% Medicare Levy
  const medicareLevy = Math.round(netTaxableIncome * 0.02);
  const netTakeHomeAnnual = Math.round(netTaxableIncome - newTotalTax - medicareLevy);

  return {
    totalGrossIncome: Math.round(totalGrossIncome),
    businessUsePercent: Math.round(validBusinessUseRatio * 100),
    cappedWriteOffDeduction: Math.round(cappedWriteOffDeduction),
    netTaxableIncome: Math.round(netTaxableIncome),
    oldTotalTax: Math.round(oldTotalTax),
    newTotalTax: Math.round(newTotalTax),
    taxCutSavings15Percent,
    writeOffTaxSavings,
    totalCombinedSavings,
    medicareLevy,
    netTakeHomeAnnual,
    legislationNote: "Effective 1 July 2026: 15% Lowest Rate ($18,201–$45k) + Permanent $20,000 Instant Asset Write-Off"
  };
}

/**
 * Australia Fair Work Minimum Wage & Modern Award Increase Simulator (FY 2026-27)
 * Sourced from Fair Work Commission 2026 Annual Wage Review (Effective 1 July 2026).
 * National Minimum Wage: $26.44 / hr ($1,004.90 / 38-hour week).
 * Modern Award Increase: 4.75% on base rates.
 * Casual Loading: 25% (Casual minimum rate: $33.05 / hr).
 */
export function calculateFairWorkMinimumWageIncrease(
  employmentType: "award_fulltime" | "award_casual" | "national_minimum_fulltime" | "national_minimum_casual" = "award_fulltime",
  currentHourlyRate: number = 30.00,
  weeklyHours: number = 38
) {
  const nmwBaseRate = 26.44; // $26.44/hr National Minimum Wage
  let newBaseHourlyRate = currentHourlyRate || 30.00;
  let increasePercent = 4.75;
  let categoryLabel = "Modern Award Wage Tier (4.75% Increase)";

  if (employmentType === "national_minimum_fulltime" || employmentType === "national_minimum_casual") {
    increasePercent = 6.0; // ~6% jump to $26.44
    newBaseHourlyRate = Math.max(nmwBaseRate, (currentHourlyRate || 24.95) * 1.06);
    categoryLabel = "National Minimum Wage ($26.44/hr Baseline)";
  } else {
    newBaseHourlyRate = Math.max(nmwBaseRate, (currentHourlyRate || 30.00) * 1.0475);
  }

  const isCasual = employmentType === "award_casual" || employmentType === "national_minimum_casual";
  const casualLoadingFactor = isCasual ? 1.25 : 1.0;

  const effectiveHourlyRate = newBaseHourlyRate * casualLoadingFactor;
  const oldEffectiveHourlyRate = (currentHourlyRate || 30.00) * casualLoadingFactor;

  const hourlyIncreaseDollars = effectiveHourlyRate - oldEffectiveHourlyRate;

  const validHours = Math.max(1, Math.min(70, weeklyHours || 38));
  const newWeeklyEarnings = effectiveHourlyRate * validHours;
  const oldWeeklyEarnings = oldEffectiveHourlyRate * validHours;
  const weeklyIncreaseDollars = newWeeklyEarnings - oldWeeklyEarnings;
  const annualIncreaseDollars = weeklyIncreaseDollars * 52;

  const casualLoadingPortion = isCasual ? newBaseHourlyRate * 0.25 * validHours : 0;

  return {
    currentHourlyRate: Number(currentHourlyRate.toFixed(2)),
    newBaseHourlyRate: Number(newBaseHourlyRate.toFixed(2)),
    effectiveHourlyRate: Number(effectiveHourlyRate.toFixed(2)),
    hourlyIncreaseDollars: Number(hourlyIncreaseDollars.toFixed(2)),
    weeklyHours: validHours,
    weeklyGrossBase: Number(oldWeeklyEarnings.toFixed(2)),
    weeklyGrossNew: Number(newWeeklyEarnings.toFixed(2)),
    newWeeklyEarnings: Number(newWeeklyEarnings.toFixed(2)),
    weeklyIncreaseDollars: Number(weeklyIncreaseDollars.toFixed(2)),
    annualIncreaseDollars: Number(annualIncreaseDollars.toFixed(2)),
    annualGrossIncrease: Math.round(annualIncreaseDollars),
    annualSuperBoost: Math.round(annualIncreaseDollars * 0.12),
    casualLoadingPortion: Number(casualLoadingPortion.toFixed(2)),
    increasePercent,
    categoryLabel,
    fairWorkNotice: "Fair Work Commission 2026 Annual Wage Review (Effective 1 July 2026)"
  };
}




